import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { erfuelltRecht } from '@symphenia/shared-types'
import { fmtDateGroupHeader, fmtTimeUTC, fmtMonthYear, fmtWeekRange, fmtDateLong } from '@/lib/format-date'
import { useHoursFormat, formatHoursWithUnit } from '@/lib/hours-format'
import { LoadingOverlay } from '@/components/ui/LoadingOverlay'
import { apiErrorText } from '@/lib/api-error'
import { Pencil, Trash2, Clock, Receipt, CalendarOff, User as UserIcon, Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuthStore } from '@/lib/auth.store'
import { usersApi } from '@/lib/users/users.api'
import type { User } from '@/lib/users/user.types'
import { absencesApi, absenceTypesApi } from '@/features/absence/api/absence.api'
import type { Absence, AbsenceType } from '@/features/absence/types/absence.types'
import DailyRecordDialog from '../components/DailyRecordDialog'
import PrivacyConsentModal from '../components/PrivacyConsentModal'
import { SearchSelect } from '../components/SearchSelect'
import {
  timeEntriesApi,
  timeConfigApi,
  dailyRecordsApi,
  privacyConsentApi,
} from '../api/time-tracking.api'
import { useBookingDataSource, usesRefLayer } from '../hooks/useBookingDataSource'
import { useTerminology } from '@/lib/useTerminology'
import type {
  TimeEntry,
  DailyRecord,
  ActivityType,
  CustomFieldSchema,
  DayFieldDef,
  StandardFieldDefinition,
  StandardSelectionItem,
  ArbeitsschrittLookupResult,
  SelectionDataRecord,
  ClockInPayload,
  ClockOutPayload,
  CreateTimeEntryPayload,
  EditPolicy,
  UpdateTimeEntryPayload,
  TimeSummary,
} from '../types/time-tracking.types'
import { pageWide } from '@/lib/form-styles'
import { bestaetige } from '@/lib/dialoge'

/**
 * Häufigste Start- und Endzeit (HH:MM) aus vergangenen Einträgen ableiten.
 * Zeiten werden auf 15 Minuten gerundet, damit leicht schwankende
 * (gestempelte) Zeiten zu stabilen Vorgabewerten zusammenfallen.
 * Liefert null, wenn keine brauchbaren Start-/Endzeiten vorhanden sind.
 */
function mostFrequentTimes(
  entries: { startTime?: string | null; endTime?: string | null }[],
): { start: string; end: string } | null {
  const roundTo15 = (hhmm: string): string => {
    const [h, m] = hhmm.split(':').map(Number)
    if (Number.isNaN(h) || Number.isNaN(m)) return hhmm
    let total = Math.round((h * 60 + m) / 15) * 15
    if (total >= 24 * 60) total = 24 * 60 - 15
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
  }
  const mode = (times: string[]): string | null => {
    const counts = new Map<string, number>()
    for (const t of times) counts.set(t, (counts.get(t) ?? 0) + 1)
    let best: string | null = null
    let bestCount = 0
    for (const [t, n] of counts) if (n > bestCount) { best = t; bestCount = n }
    return best
  }
  const starts: string[] = []
  const ends: string[] = []
  for (const e of entries) {
    const s = e.startTime ? fmtTimeUTC(e.startTime) : ''
    if (s) starts.push(roundTo15(s))
    const en = e.endTime ? fmtTimeUTC(e.endTime) : ''
    if (en) ends.push(roundTo15(en))
  }
  const start = mode(starts)
  const end = mode(ends)
  return start && end ? { start, end } : null
}



// Anzeigename eines Mitarbeiters (Vor-/Nachname, sonst E-Mail).
function fmtEmployeeName(u: User): string {
  return u.profile?.firstName
    ? `${u.profile.firstName}${u.profile.lastName ? ' ' + u.profile.lastName : ''}`
    : u.email ?? '—'
}

// Initialen für das Avatar (max. 2 Buchstaben).
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

// AP-19: Plausible Vorgabe-Stundenzahl aus vergangenen Fehlzeiten des Mitarbeiters
// ableiten. Bevorzugt Einträge desselben Fehlzeit-Typs; nimmt den häufigsten
// Stundenwert (Modus), bei Gleichstand den jüngsten (Liste ist desc sortiert).
// null → keine Basis vorhanden (dann bleibt der statische Default bestehen).
function suggestAbsenceHours(list: Absence[], typeId: string): string | null {
  const pool = list.filter((a) => a.durationMinutes > 0)
  if (pool.length === 0) return null
  const sameType = typeId ? pool.filter((a) => a.absenceTypeId === typeId) : []
  const relevant = sameType.length > 0 ? sameType : pool
  const counts = new Map<number, number>()
  for (const a of relevant) counts.set(a.durationMinutes, (counts.get(a.durationMinutes) ?? 0) + 1)
  let bestMin = relevant[0].durationMinutes
  let bestCount = 0
  for (const [min, n] of counts) if (n > bestCount) { bestMin = min; bestCount = n }
  return String(bestMin / 60)
}

export default function ClockPage() {
  const { t } = useTranslation()
  // AP-16: „Projekt" oder „Auftrag" — der Mandant entscheidet.
  const terms = useTerminology()
  // Sammelzeile der Tagesübersicht für Buchungen ohne Zuordnung.
  const ohneProjektLabel = `Ohne ${terms.project}`
  const { user } = useAuthStore()
  const hoursFormat = useHoursFormat()

  // READONLY ("Nur lesen"): darf die Tages-/Perioden-Zusammenfassungen einsehen,
  // aber nicht buchen. Buchungs-Steuerung (Stempeln, manuelle Erfassung, Diäten)
  // wird ausgeblendet; das Backend sperrt Schreibzugriffe zusätzlich mit 403.
  // AP-30-D: Nicht „ist er READONLY?", sondern „darf er buchen?". Die
  // Rolle war nur die häufigste Art, es nicht zu dürfen.
  const isReadonly = !erfuelltRecht('time.entry.create', user?.permissions)

  // MANAGER+ dürfen bei der manuellen Erfassung für andere Mitarbeiter buchen
  // (wie in der Fehlzeit-Erfassung). Die Stempeluhr bleibt immer persönlich.
  // Fremdbuchung: für jemand anderen erfassen — ein eigenes Recht, nicht
  // der Lese-Geltungsbereich (AP-30, Fallen).
  const isManager = erfuelltRecht('time.booking.for_others', user?.permissions)

  // Ausgewählter Zielmitarbeiter der manuellen Erfassung ('' = Ich selbst).
  // Bleibt nach Auswahl und nach dem Buchen erhalten; beim erneuten Öffnen der
  // Seite steht der State wieder auf '' (Ich selbst).
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('')
  const [employees, setEmployees] = useState<User[]>([])

  // Tab state — Stempeluhr | Manuelle Erfassung | Diäten | Fehlzeiten
  const [mode, setMode] = useState<'clock' | 'manual' | 'diaeten' | 'fehlzeit'>('clock')

  // Reiterwechsel: die Mitarbeiter-Auswahl gilt für alle Reiter außer der
  // Stempeluhr. Beim Wechsel auf die Stempeluhr springt sie auf „Ich selbst"
  // zurück (Anforderung: Stempeluhr gilt nur für den angemeldeten Mitarbeiter).
  const switchMode = (next: 'clock' | 'manual' | 'diaeten' | 'fehlzeit') => {
    if (next === 'clock') setSelectedEmployeeId('')
    // Fehlzeit-Reiter frisch geöffnet → Stundenvorschlag wieder zulassen.
    if (next === 'fehlzeit') setFzHoursTouched(false)
    setMode(next)
  }

  // Segment-Kachel: aktive gefüllt, inaktive dezent; Icon über kurzem Label.
  const tabCls = (m: 'clock' | 'manual' | 'diaeten' | 'fehlzeit') =>
    `flex flex-col items-center justify-center gap-1 rounded-lg px-1 py-2 text-center transition-colors ${
      mode === m
        ? 'bg-primary text-primary-foreground shadow-sm'
        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
    }`

  // Effektiver Mitarbeiter für Tages-/Perioden-Zusammenfassungen und die
  // Tageseinträge: in allen Erfassungsreitern der gewählte Mitarbeiter, in der
  // Stempeluhr immer der angemeldete Nutzer.
  const summaryUserId =
    isManager && mode !== 'clock' && selectedEmployeeId ? selectedEmployeeId : user?.id
  // Für Schreib-/Ladepfade der Erfassungsreiter: nur eine echte Fremdauswahl
  // liefert eine ID, sonst undefined (= angemeldeter Nutzer).
  const targetEmployeeId = isManager && mode !== 'clock' && selectedEmployeeId ? selectedEmployeeId : undefined

  // Kompakte Mitarbeiter-Auswahl (über den Reitern): aktueller Zielmitarbeiter
  // + Kennzeichen, ob gerade für eine ANDERE Person gebucht wird (Warn-Optik).
  const selectedEmployee = employees.find((u) => u.id === selectedEmployeeId) ?? null
  const isForeignBooking = isManager && mode !== 'clock' && !!selectedEmployeeId

  // Lokales Datum als YYYY-MM-DD — NICHT toISOString() verwenden (gibt UTC zurück, Timezone-Bug in UTC+x)
  const toLocalDateStr = (dt: Date): string =>
    `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`

  // Lokale Wall-Clock-Uhrzeit als HH:MM (für clientTime an clockIn/clockOut)
  const toLocalTimeStr = (dt: Date): string =>
    `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`

  // Tagesnavigation – welcher Tag wird gerade angezeigt
  const todayStr = toLocalDateStr(new Date())
  const [viewDate, setViewDate] = useState(todayStr)

  // ── Diäten-Reiter ────────────────────────────────────────────────────────────
  const [diaetenDate, setDiaetenDate] = useState(todayStr)

  // ── Fehlzeit-Reiter (Erfassung wie in der PWA) ───────────────────────────────
  const [absenceTypes, setAbsenceTypes] = useState<AbsenceType[]>([])
  const [fzTypeId, setFzTypeId] = useState('')
  const [fzDate, setFzDate] = useState(todayStr)
  const [fzHours, setFzHours] = useState('8')
  // Sobald der Nutzer die Stundenzahl selbst ändert, kein Auto-Vorschlag mehr.
  const [fzHoursTouched, setFzHoursTouched] = useState(false)
  const [fzNote, setFzNote] = useState('')
  const [fzSaving, setFzSaving] = useState(false)
  const [fzError, setFzError] = useState<string | null>(null)
  const [fzSuccess, setFzSuccess] = useState(false)
  const [recentAbsences, setRecentAbsences] = useState<Absence[]>([])

  const navigatePrevDay = () => {
    setViewDate((d) => {
      const date = new Date(d)
      date.setDate(date.getDate() - 1)
      return date.toISOString().split('T')[0]
    })
  }
  const navigateNextDay = () => {
    setViewDate((d) => {
      const date = new Date(d)
      date.setDate(date.getDate() + 1)
      return date.toISOString().split('T')[0]
    })
  }
  const formatViewDate = (dateStr: string) => fmtDateGroupHeader(dateStr)

  // Clock mode state
  const [activeEntry, setActiveEntry] = useState<TimeEntry | null>(null)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [clockSelectedProject, setClockSelectedProject] = useState('')
  const [clockSelectedActivity, setClockSelectedActivity] = useState('')
  const [clockNote, setClockNote] = useState('')
  const [breakMinutes, setBreakMinutes] = useState(0)
  const [clockLoading, setClockLoading] = useState(false)

  // Manual mode state
  const [manualDate, setManualDate] = useState(
    toLocalDateStr(new Date())
  )
  const [manualStartTime, setManualStartTime] = useState('09:00')
  const [manualEndTime, setManualEndTime] = useState('17:00')
  // Vorgabezeiten aus den häufigsten vergangenen Einträgen (siehe Lade-Effekt).
  // Dienen als Ausgangswert der manuellen Buchung UND als Reset-Ziel nach dem
  // Speichern. Start bei den bisherigen Hardcode-Defaults.
  const [defaultManualStart, setDefaultManualStart] = useState('09:00')
  const [defaultManualEnd, setDefaultManualEnd] = useState('17:00')
  const [manualSelectedProject, setManualSelectedProject] = useState('')
  const [manualSelectedActivity, setManualSelectedActivity] = useState('')
  const [manualBreak, setManualBreak] = useState(0)
  const [manualNote, setManualNote] = useState('')
  const [manualLoading, setManualLoading] = useState(false)

  // Typ-A custom fields state (Zeiterfassung – pro Buchung)
  const [timeEntrySchema, setTimeEntrySchema] = useState<CustomFieldSchema[]>([])
  const [clockCustomFields, setClockCustomFields] = useState<Record<string, unknown>>({})
  const [manualCustomFields, setManualCustomFields] = useState<Record<string, unknown>>({})

  // Standard-Felder state
  const [standardFieldDefs, setStandardFieldDefs] = useState<StandardFieldDefinition[]>([])
  const [clockStandardFields, setClockStandardFields] = useState<Record<string, unknown>>({})
  const [manualStandardFields, setManualStandardFields] = useState<Record<string, unknown>>({})

  // Kaskadierte Auswahl: Projektposition + Arbeitsschritt (Clock mode)
  const [clockSelectedProjektposition, setClockSelectedProjektposition] = useState('')
  const [clockSelectedArbeitsschritt, setClockSelectedArbeitsschritt] = useState('')
  const [clockProjektpositionen, setClockProjektpositionen] = useState<StandardSelectionItem[]>([])
  const [clockArbeitsschritte, setClockArbeitsschritte] = useState<StandardSelectionItem[]>([])

  // Kaskadierte Auswahl: Projektposition + Arbeitsschritt (Manual mode)
  const [manualSelectedProjektposition, setManualSelectedProjektposition] = useState('')
  const [manualSelectedArbeitsschritt, setManualSelectedArbeitsschritt] = useState('')
  const [manualProjektpositionen, setManualProjektpositionen] = useState<StandardSelectionItem[]>([])
  const [manualArbeitsschritte, setManualArbeitsschritte] = useState<StandardSelectionItem[]>([])

  // V2: IDArbeitsschritt-Lookup (Clock mode)
  const [clockV2Id, setClockV2Id] = useState('')
  const [clockV2Loading, setClockV2Loading] = useState(false)
  const [clockV2Error, setClockV2Error] = useState<string | null>(null)
  const [clockV2Result, setClockV2Result] = useState<ArbeitsschrittLookupResult | null>(null)

  // V2: IDArbeitsschritt-Lookup (Manual mode)
  const [manualV2Id, setManualV2Id] = useState('')
  const [manualV2Loading, setManualV2Loading] = useState(false)
  const [manualV2Error, setManualV2Error] = useState<string | null>(null)
  const [manualV2Result, setManualV2Result] = useState<ArbeitsschrittLookupResult | null>(null)

  // Buchungsansatz: 'v1' (Auswahl kaskadiert) oder 'v2' (ID-Eingabe)
  const [bookingMode, setBookingMode] = useState<'v1' | 'v2'>('v1')

  // Projekt-Combobox (Nummer · Bezeichnung – kombiniertes Eingabe-/Suchfeld)

  // Betriebsstätte-Auswahlliste
  const [betriebsstaetteItems, setBetriebsstaetteItems] = useState<StandardSelectionItem[]>([])

  // Vorgabedatensätze für Zusatzfeld-Gruppen (groupName → records)
  const [groupRecords, setGroupRecords] = useState<Record<string, SelectionDataRecord[]>>({})

  // Diäten (Tagesdaten) state
  const [dailyRecord, setDailyRecord] = useState<DailyRecord | null>(null)
  const [dailyRecordSchema, setDailyRecordSchema] = useState<CustomFieldSchema[]>([])
  // AP-24/AP-26: Modus des Monatsberichts und die eingeschalteten Tagesfelder.
  // Beides kommt aus clock-settings — `GET /time-config` ist ADMIN-only und
  // liefert Buchungsrollen still einen 403 (KA-03/KA-08).
  const [manualHours, setManualHours] = useState(false)
  const [dayFields, setDayFields] = useState<DayFieldDef[]>([])

  // Perioden-Zusammenfassungen – je eigener Anker-State
  const [weekAnchor, setWeekAnchor] = useState(todayStr)
  const [monthAnchor, setMonthAnchor] = useState(todayStr)
  const [yearAnchor, setYearAnchor] = useState(todayStr)
  const [weekSummary, setWeekSummary] = useState<TimeSummary | null>(null)
  const [monthSummary, setMonthSummary] = useState<TimeSummary | null>(null)
  const [yearSummary, setYearSummary] = useState<TimeSummary | null>(null)
  const [weekDailyRecords, setWeekDailyRecords] = useState<DailyRecord[]>([])
  const [monthDailyRecords, setMonthDailyRecords] = useState<DailyRecord[]>([])
  const [yearDailyRecords, setYearDailyRecords] = useState<DailyRecord[]>([])
  // AP-19: Fehlzeiten je Zeitraum (persönlich) + Wochen-Einträge für Projektsummen
  const [dayAbsences, setDayAbsences] = useState<Absence[]>([])
  const [weekAbsences, setWeekAbsences] = useState<Absence[]>([])
  const [monthAbsences, setMonthAbsences] = useState<Absence[]>([])
  const [yearAbsences, setYearAbsences] = useState<Absence[]>([])
  const [weekEntries, setWeekEntries] = useState<TimeEntry[]>([])
  // Ladeanzeige je Zeitraum (verhindert den Eindruck „alte Daten bleiben stehen")
  const [weekLoading, setWeekLoading] = useState(false)
  const [monthLoading, setMonthLoading] = useState(false)
  const [yearLoading, setYearLoading] = useState(false)

  // Woche navigieren (± 7 Tage)
  const navigatePrevWeek = () => setWeekAnchor(d => { const dt = new Date(d + 'T00:00:00'); dt.setDate(dt.getDate() - 7); return toLocalDateStr(dt) })
  const navigateNextWeek = () => setWeekAnchor(d => { const dt = new Date(d + 'T00:00:00'); dt.setDate(dt.getDate() + 7); return toLocalDateStr(dt) })

  // Monat navigieren (± 1 Monat, bleibt am 1. des Monats)
  const navigatePrevMonth = () => setMonthAnchor(d => { const dt = new Date(d + 'T00:00:00'); dt.setMonth(dt.getMonth() - 1); return toLocalDateStr(dt) })
  const navigateNextMonth = () => setMonthAnchor(d => { const dt = new Date(d + 'T00:00:00'); dt.setMonth(dt.getMonth() + 1); return toLocalDateStr(dt) })

  // Jahr navigieren (± 1 Jahr)
  const navigatePrevYear = () => setYearAnchor(d => { const dt = new Date(d + 'T00:00:00'); dt.setFullYear(dt.getFullYear() - 1); return toLocalDateStr(dt) })
  const navigateNextYear = () => setYearAnchor(d => { const dt = new Date(d + 'T00:00:00'); dt.setFullYear(dt.getFullYear() + 1); return toLocalDateStr(dt) })

  // Format-Helpers für Perioden-Header
  const formatWeekHeader = (dateStr: string) => {
    const ranges = getPeriodRanges(dateStr)
    const from = new Date(ranges.week.from + 'T00:00:00')
    const to   = new Date(ranges.week.to   + 'T00:00:00')
    // ISO-Kalenderwoche
    const jan4 = new Date(from.getFullYear(), 0, 4)
    const kw = Math.ceil(((from.getTime() - jan4.getTime()) / 86400000 + jan4.getDay() + 1) / 7)
    return fmtWeekRange(from, to, kw)
  }
  const formatMonthHeader = (dateStr: string) => fmtMonthYear(dateStr)
  const formatYearHeader = (dateStr: string) =>
    String(new Date(dateStr + 'T00:00:00').getFullYear())

  // Max-Werte für date-Inputs
  const todayYearMonth = todayStr.slice(0, 7)          // YYYY-MM
  const todayYear = new Date().getFullYear()

  // Data fetching state
  const [entries, setEntries] = useState<TimeEntry[]>([])
  const [projects, setProjects] = useState<{ id: string; name: string; projectNumber: string; externalId: string | null }[]>([])
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [manualError, setManualError] = useState<string | null>(null)

  // Allgemeine Einstellungen (aus TenantTimeConfig)
  const [configAllowedModes, setConfigAllowedModes] = useState<string[]>(['CLOCK', 'MANUAL'])
  const [configAllowNotes, setConfigAllowNotes] = useState(true)
  // Admin-Schalter: Pausenminuten-Feld in den Buchungsmasken nutzen
  const [breakMinutesEnabled, setBreakMinutesEnabled] = useState(true)
  // Edit-Policy (aus TenantTimeConfig)
  const [editPolicy, setEditPolicy] = useState<EditPolicy>('LOCKED')
  const [editWindowHours, setEditWindowHours] = useState<number | null>(48)
  // AP-06-A: Privacy Consent
  const [privacyText, setPrivacyText] = useState<string | null>(null)
  const [privacyUrl, setPrivacyUrl] = useState<string | null>(null)
  const [consentRequired, setConsentRequired] = useState(false)
  // Ob überhaupt eine Datenschutzerklärung hinterlegt ist (steuert Modal-Inhalt)
  const [consentConfigured, setConsentConfigured] = useState(true)
  // Modal weggeklickt, aber noch nicht zugestimmt → Seite bleibt gesperrt
  const [consentModalDismissed, setConsentModalDismissed] = useState(false)

  // Zeiteintrag bearbeiten
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState({
    startTime: '',
    endTime: '',
    breakMinutes: 0,
    note: '',
    editNote: '',
    projectId: '',
    activityTypeId: '',
    projectRefId: '',
    activityRefId: '',
    // Kaskaden-IDs (Projektposition / Arbeitsschritt) — identisch zur Buchungsmaske
    selectedProjektposition: '',
    selectedArbeitsschritt: '',
    standardFields: {} as Record<string, string>,
    customFields: {} as Record<string, unknown>,
  })
  // Kaskade Edit (Legacy-Modus): Projektpositionen / Arbeitsschritte nachladen
  const [editProjektpositionen, setEditProjektpositionen] = useState<StandardSelectionItem[]>([])
  const [editArbeitsschritte, setEditArbeitsschritte] = useState<StandardSelectionItem[]>([])
  const [editLoading, setEditLoading] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)

  // Zeiteintrag löschen
  const [deletingEntryId, setDeletingEntryId] = useState<string | null>(null)
  const [deleteLoading, setDeleteLoading] = useState(false)

  // Fetch Typ-A custom fields schema
  const fetchTimeEntrySchema = useCallback(async () => {
    try {
      const { data } = await timeConfigApi.getTimeEntryCustomFieldsSchema()
      const schema = Array.isArray(data) ? data : []
      setTimeEntrySchema(schema)
      // Initialize empty values
      const initial: Record<string, unknown> = {}
      schema.forEach((f) => { initial[f.name] = f.type === 'checkbox' ? false : '' })
      setClockCustomFields(initial)
      setManualCustomFields({ ...initial })
    } catch (err) {
      console.error('Failed to fetch time entry custom fields schema', err)
    }
  }, [])

  // Fetch standard field definitions (visible, non-ID fields for the form)
  const fetchStandardFieldDefs = useCallback(async () => {
    try {
      const { data } = await timeConfigApi.getStandardFields()
      const defs = Array.isArray(data) ? data : []
      setStandardFieldDefs(defs)
      // Initialize empty values for all standard fields (including hidden ID fields)
      const initial: Record<string, unknown> = {}
      defs.forEach((f) => { initial[f.name] = f.type === 'number' ? '' : '' })
      setClockStandardFields(initial)
      setManualStandardFields({ ...initial })
    } catch (err) {
      console.error('Failed to fetch standard field definitions', err)
    }
  }, [])

  // Fetch projects
  const fetchProjects = useCallback(async () => {
    try {
      const { data } = await timeConfigApi.getProjects()
      setProjects(data)
    } catch (err) {
      console.error('Failed to fetch projects', err)
    }
  }, [])

  // Fetch Betriebsstätte items for dropdown
  const fetchBetriebsstaetteItems = useCallback(async () => {
    try {
      const { data } = await timeConfigApi.getStandardSelectionItems('betriebsstaette')
      setBetriebsstaetteItems(Array.isArray(data) ? data.filter(i => i.isActive) : [])
    } catch {
      // not configured – ignore
    }
  }, [])

  // Vorgabedatensätze für alle Gruppen in timeEntrySchema laden
  useEffect(() => {
    const groupNames = [...new Set(
      timeEntrySchema.filter((f) => f.group).map((f) => f.group!)
    )]
    if (groupNames.length === 0) return
    groupNames.forEach((groupName) => {
      timeConfigApi.getSelectionDataRecords(groupName)
        .then(({ data }) => {
          setGroupRecords((prev) => ({
            ...prev,
            [groupName]: (data ?? []).filter((r) => r.isActive),
          }))
        })
        .catch(() => {})
    })
  }, [timeEntrySchema])

  // ── AP-09-E: Ref-Layer Datenquelle (EXTERNAL_API + INTERNAL) ─────────────
  // Liefert mode + (im Ref-Modus) refProjects/refPositions/refSteps/refActivities
  // sowie buildBookingIds(...) für die submit-Pfade. Nur im MANUAL-Modus bleiben
  // die ref*-Listen leer und buildBookingIds routet auf Legacy-IDs.
  const clockBookingDS = useBookingDataSource({
    cascadePositions: true,
    selectedProjectRefId: clockSelectedProject || null,
    selectedPositionRefId: clockSelectedProjektposition || null,
  })
  const manualBookingDS = useBookingDataSource({
    cascadePositions: true,
    selectedProjectRefId: manualSelectedProject || null,
    selectedPositionRefId: manualSelectedProjektposition || null,
  })
  // Eigene Datenquelle für das Inline-Edit-Formular — kaskadiert über die im
  // Edit gewählten Projekt-/Positions-IDs (Ref-Modus), exakt wie die Buchungsmaske.
  const editBookingDS = useBookingDataSource({
    cascadePositions: true,
    selectedProjectRefId: editForm.projectRefId || null,
    selectedPositionRefId: editForm.selectedProjektposition || null,
  })
  const clockProjectIsRef    = !!clockBookingDS.mode  && usesRefLayer(clockBookingDS.mode.project)
  const clockActivityIsRef   = !!clockBookingDS.mode  && usesRefLayer(clockBookingDS.mode.activityType)
  const manualProjectIsRef   = !!manualBookingDS.mode && usesRefLayer(manualBookingDS.mode.project)
  const manualActivityIsRef  = !!manualBookingDS.mode && usesRefLayer(manualBookingDS.mode.activityType)

  // Effektive Listen fürs Rendering — Ref- oder Legacy-Quelle je nach Modus.
  // Beide Quellen haben kompatible Felder {id, projectNumber, name} bzw. {id, name}.
  type ProjectLike = { id: string; projectNumber: string; name: string; externalId?: string | null }
  type ActivityLike = { id: string; name: string; color?: string | null }
  type PosStepLike = { id: string; nummer: number | null; name: string }
  const clockProjectsUI:   ProjectLike[]  = clockProjectIsRef  ? clockBookingDS.refProjects   : (projects as unknown as ProjectLike[])
  const clockActivitiesUI: ActivityLike[] = clockActivityIsRef ? clockBookingDS.refActivities : (activityTypes as unknown as ActivityLike[])
  const clockPositionsUI:  PosStepLike[]  = clockProjectIsRef  ? clockBookingDS.refPositions.map(p => ({ id: p.id, nummer: p.nummer, name: p.name })) : clockProjektpositionen
  const clockStepsUI:      PosStepLike[]  = clockProjectIsRef  ? clockBookingDS.refSteps.map(s => ({ id: s.id, nummer: s.nummer, name: s.name }))     : clockArbeitsschritte
  const manualProjectsUI:   ProjectLike[]  = manualProjectIsRef  ? manualBookingDS.refProjects   : (projects as unknown as ProjectLike[])
  const manualActivitiesUI: ActivityLike[] = manualActivityIsRef ? manualBookingDS.refActivities : (activityTypes as unknown as ActivityLike[])
  const manualPositionsUI:  PosStepLike[]  = manualProjectIsRef  ? manualBookingDS.refPositions.map(p => ({ id: p.id, nummer: p.nummer, name: p.name })) : manualProjektpositionen
  const manualStepsUI:      PosStepLike[]  = manualProjectIsRef  ? manualBookingDS.refSteps.map(s => ({ id: s.id, nummer: s.nummer, name: s.name }))     : manualArbeitsschritte
  // Effektive Listen fürs Inline-Edit (gleiche Ref/Legacy-Routing-Logik wie oben)
  const editPositionsUI: PosStepLike[] = clockProjectIsRef  ? editBookingDS.refPositions.map(p => ({ id: p.id, nummer: p.nummer, name: p.name })) : editProjektpositionen
  const editStepsUI:     PosStepLike[] = clockProjectIsRef  ? editBookingDS.refSteps.map(s => ({ id: s.id, nummer: s.nummer, name: s.name }))     : editArbeitsschritte

  // Kaskade Clock: Projektpositionen laden wenn Projekt gewählt
  useEffect(() => {
    if (!clockSelectedProject) {
      setClockProjektpositionen([])
      setClockSelectedProjektposition('')
      setClockArbeitsschritte([])
      setClockSelectedArbeitsschritt('')
      return
    }
    // Im Ref-Modus übernimmt useBookingDataSource das Laden — Legacy-Endpoint überspringen
    if (clockProjectIsRef) return
    timeConfigApi.getProjektpositionenByProject(clockSelectedProject)
      .then(({ data }) => setClockProjektpositionen(data ?? []))
      .catch(() => setClockProjektpositionen([]))
  }, [clockSelectedProject, clockProjectIsRef])

  // Kaskade Clock: Arbeitsschritte laden wenn Projektposition gewählt
  useEffect(() => {
    if (!clockSelectedProjektposition) {
      setClockArbeitsschritte([])
      setClockSelectedArbeitsschritt('')
      return
    }
    if (clockProjectIsRef) return
    timeConfigApi.getArbeitsschritteByProjektposition(clockSelectedProjektposition)
      .then(({ data }) => setClockArbeitsschritte(data ?? []))
      .catch(() => setClockArbeitsschritte([]))
  }, [clockSelectedProjektposition, clockProjectIsRef])

  // Kaskade Manual: Projektpositionen laden wenn Projekt gewählt
  useEffect(() => {
    if (!manualSelectedProject) {
      setManualProjektpositionen([])
      setManualSelectedProjektposition('')
      setManualArbeitsschritte([])
      setManualSelectedArbeitsschritt('')
      return
    }
    if (manualProjectIsRef) return
    timeConfigApi.getProjektpositionenByProject(manualSelectedProject)
      .then(({ data }) => setManualProjektpositionen(data ?? []))
      .catch(() => setManualProjektpositionen([]))
  }, [manualSelectedProject, manualProjectIsRef])

  // Kaskade Manual: Arbeitsschritte laden wenn Projektposition gewählt
  useEffect(() => {
    if (!manualSelectedProjektposition) {
      setManualArbeitsschritte([])
      setManualSelectedArbeitsschritt('')
      return
    }
    if (manualProjectIsRef) return
    timeConfigApi.getArbeitsschritteByProjektposition(manualSelectedProjektposition)
      .then(({ data }) => setManualArbeitsschritte(data ?? []))
      .catch(() => setManualArbeitsschritte([]))
  }, [manualSelectedProjektposition, manualProjectIsRef])

  // Kaskade Edit (Legacy): Projektpositionen laden wenn Projekt im Edit gewählt.
  // Im Ref-Modus übernimmt editBookingDS das Laden (editPositionsUI).
  useEffect(() => {
    if (clockProjectIsRef) return
    if (!editForm.projectId) {
      setEditProjektpositionen([])
      return
    }
    timeConfigApi.getProjektpositionenByProject(editForm.projectId)
      .then(({ data }) => setEditProjektpositionen(data ?? []))
      .catch(() => setEditProjektpositionen([]))
  }, [editForm.projectId, clockProjectIsRef])

  // Kaskade Edit (Legacy): Arbeitsschritte laden wenn Projektposition im Edit gewählt.
  useEffect(() => {
    if (clockProjectIsRef) return
    if (!editForm.selectedProjektposition) {
      setEditArbeitsschritte([])
      return
    }
    timeConfigApi.getArbeitsschritteByProjektposition(editForm.selectedProjektposition)
      .then(({ data }) => setEditArbeitsschritte(data ?? []))
      .catch(() => setEditArbeitsschritte([]))
  }, [editForm.selectedProjektposition, clockProjectIsRef])

  // Legacy-Edit: gespeicherte Snapshot-Namen (projektposition/arbeitsschritt) auf
  // die gerade geladenen Kaskaden-IDs zurückmappen, damit die Dropdowns vorbelegt sind.
  useEffect(() => {
    if (clockProjectIsRef || editingEntryId === null) return
    if (editForm.selectedProjektposition || editPositionsUI.length === 0) return
    const ppName = editForm.standardFields['projektposition']
    if (!ppName) return
    const match = editPositionsUI.find((p) => p.name === ppName)
    if (match) setEditForm((p) => ({ ...p, selectedProjektposition: match.id }))
  }, [editPositionsUI, clockProjectIsRef, editingEntryId, editForm.selectedProjektposition, editForm.standardFields])

  useEffect(() => {
    if (clockProjectIsRef || editingEntryId === null) return
    if (editForm.selectedArbeitsschritt || editStepsUI.length === 0) return
    const asName = editForm.standardFields['arbeitsschritt']
    if (!asName) return
    const match = editStepsUI.find((s) => s.name === asName)
    if (match) setEditForm((p) => ({ ...p, selectedArbeitsschritt: match.id }))
  }, [editStepsUI, clockProjectIsRef, editingEntryId, editForm.selectedArbeitsschritt, editForm.standardFields])

  // Fetch activity types
  const fetchActivityTypes = useCallback(async () => {
    try {
      const { data } = await timeConfigApi.getActivityTypes()
      setActivityTypes(data)
    } catch (err) {
      console.error('Failed to fetch activity types', err)
      setError(t('timeTracking.errorLoadingActivityTypes', { defaultValue: 'Tätigkeitsarten konnten nicht geladen werden.' }))
    }
  }, [t])

  // Fetch entries for the currently viewed date
  // WICHTIG: userId immer mitschicken — die Stempeluhr ist eine persönliche
  // Seite. Ohne Filter liefert das Backend bei MANAGER/ADMIN alle Einträge
  // des Tenants → fremde Buchungen in der Tageszusammenfassung.
  const fetchEntries = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await timeEntriesApi.list({
        dateFrom: viewDate,
        dateTo: viewDate,
        userId: summaryUserId,
      })
      // Tageszusammenfassung immer nach Startzeit sortieren (älteste oben)
      const sorted = Array.isArray(data.data)
        ? [...data.data].sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
        : data.data
      setEntries(sorted)
    } catch (err) {
      console.error('Failed to fetch entries', err)
      setError(t('timeTracking.errorLoadingEntries', { defaultValue: 'Zeiteinträge konnten nicht geladen werden.' }))
    } finally {
      setLoading(false)
    }
  }, [t, viewDate, summaryUserId])

  // Vorgabezeiten für die manuelle Buchung aus den häufigsten Start-/Endzeiten
  // der letzten 30 Tage ableiten (einmalig beim Laden). Nur setzen, solange die
  // Felder unverändert auf den Hardcode-Defaults stehen — eine bereits getätigte
  // Nutzereingabe wird nicht überschrieben. READONLY bucht nicht → übersprungen.
  const manualDefaultsInit = useRef(false)
  useEffect(() => {
    if (isReadonly || !user?.id || manualDefaultsInit.current) return
    manualDefaultsInit.current = true
    const to = toLocalDateStr(new Date())
    const fromDt = new Date(); fromDt.setDate(fromDt.getDate() - 30)
    const from = toLocalDateStr(fromDt)
    timeEntriesApi.list({ dateFrom: from, dateTo: to, limit: 200, userId: user.id })
      .then(({ data }) => {
        const list = Array.isArray(data.data) ? data.data : []
        const freq = mostFrequentTimes(list)
        if (!freq) return
        setDefaultManualStart(freq.start)
        setDefaultManualEnd(freq.end)
        setManualStartTime((prev) => (prev === '09:00' ? freq.start : prev))
        setManualEndTime((prev) => (prev === '17:00' ? freq.end : prev))
      })
      .catch(() => { /* keine Historie / Fehler → Hardcode-Defaults bleiben */ })
  }, [isReadonly, user?.id])

  // Fetch daily record (Diäten) for the currently viewed date (für gewählten MA)
  const fetchDailyRecord = useCallback(async () => {
    try {
      const { data } = await dailyRecordsApi.getByDate(viewDate, summaryUserId)
      setDailyRecord(data)
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number } }
      if (axiosErr?.response?.status === 404) {
        setDailyRecord(null)
      }
    }
  }, [viewDate, summaryUserId])

  // Hilfsfunktion: Datumsbereiche für Woche/Monat/Jahr berechnen
  const getPeriodRanges = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00')
    // Woche: Montag–Sonntag
    const dayOfWeek = date.getDay() === 0 ? 6 : date.getDay() - 1 // 0=Mo
    const weekStart = new Date(date)
    weekStart.setDate(date.getDate() - dayOfWeek)
    const weekEnd = new Date(weekStart)
    weekEnd.setDate(weekStart.getDate() + 6)
    // Monat
    const monthStart = new Date(date.getFullYear(), date.getMonth(), 1)
    const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0)
    // Jahr
    const yearStart = new Date(date.getFullYear(), 0, 1)
    const yearEnd = new Date(date.getFullYear(), 11, 31)
    const fmt = toLocalDateStr  // NICHT toISOString() – gibt UTC zurück, verschiebt lokale Mitternacht
    return {
      week: { from: fmt(weekStart), to: fmt(weekEnd) },
      month: { from: fmt(monthStart), to: fmt(monthEnd) },
      year: { from: fmt(yearStart), to: fmt(yearEnd) },
    }
  }

  // Hilfsfunktion: Minuten → lesbare Zeitspanne gemäß tenant-weitem Stunden-Format
  // (Dezimal max. 2 Kommastellen ohne überflüssige Nullen, oder H:MM).
  const formatHoursDecimal = (m: number) => formatHoursWithUnit(m, hoursFormat)

  // Projektbezeichnung mit vorangestellter Projektnummer (falls vorhanden).
  const projectLabel = (number: string | null | undefined, name: string) =>
    number ? `${number} ${name}` : name

  // AP-19: Fehlzeiten je Typ summieren (Wochen-/Monats-/Jahreszusammenfassung)
  const sumAbsencesByType = (list: Absence[]): { name: string; color: string | null; minutes: number }[] => {
    const map = new Map<string, { color: string | null; minutes: number }>()
    for (const a of list) {
      const name = a.absenceType?.name ?? '—'
      const cur = map.get(name) ?? { color: a.absenceType?.color ?? null, minutes: 0 }
      cur.minutes += a.durationMinutes
      map.set(name, cur)
    }
    return [...map.entries()]
      .map(([name, v]) => ({ name, color: v.color, minutes: v.minutes }))
      .sort((a, b) => b.minutes - a.minutes)
  }

  // AP-19: Zeiteinträge je Projekt summieren (Bruttozeit; Ref-Pfad vor Legacy).
  // Label = Projektnummer + Name (Nummer vorangestellt, falls vorhanden).
  const sumEntriesByProject = (list: TimeEntry[]): { name: string; minutes: number }[] => {
    const map = new Map<string, number>()
    for (const e of list) {
      const name = e.projectRef?.name ?? e.project?.name ?? null
      const number = e.projectRef?.projectNumber ?? e.project?.projectNumber ?? null
      const label = name ? projectLabel(number, name) : ohneProjektLabel
      map.set(label, (map.get(label) ?? 0) + (e.duration ?? 0))
    }
    return [...map.entries()]
      .map(([name, minutes]) => ({ name, minutes }))
      .sort((a, b) => b.minutes - a.minutes)
  }

  // Hilfsfunktion: Diäten-Felder über mehrere DailyRecords aggregieren
  // - number   → Summe
  // - select   → nach Auswahl gruppiert zählen  { mode: 'grouped', groups: { 'Auswärts': 2, 'Inland': 1 } }
  // - text/time/checkbox → Anzahl nicht-leerer Einträge
  // - ID-Felder (name enthält "id") → ignorieren
  type DiaetenEntry =
    | { mode: 'sum';     value: number }
    | { mode: 'count';   value: number }
    | { mode: 'grouped'; groups: Record<string, number> }

  const sumDiaeten = (records: DailyRecord[]): Record<string, DiaetenEntry> => {
    const totals: Record<string, DiaetenEntry> = {}
    for (const rec of records) {
      for (const [key, val] of Object.entries(rec.customFields ?? {})) {
        if (key.toLowerCase().includes('id')) continue
        const fieldDef = dailyRecordSchema.find((f) => f.name === key)
        const type = fieldDef?.type

        if (type === 'number') {
          const prev = (totals[key] ?? { mode: 'sum', value: 0 }) as { mode: 'sum'; value: number }
          const num = typeof val === 'number' ? val : parseFloat(String(val)) || 0
          totals[key] = { mode: 'sum', value: prev.value + num }

        } else if (type === 'select') {
          const prev = (totals[key] ?? { mode: 'grouped', groups: {} }) as { mode: 'grouped'; groups: Record<string, number> }
          const strVal = String(val ?? '').trim()
          if (strVal) {
            const groups = { ...prev.groups, [strVal]: (prev.groups[strVal] ?? 0) + 1 }
            totals[key] = { mode: 'grouped', groups }
          } else {
            totals[key] = prev
          }

        } else {
          const prev = (totals[key] ?? { mode: 'count', value: 0 }) as { mode: 'count'; value: number }
          const hasValue = typeof val === 'boolean' ? val : (val !== '' && val != null)
          totals[key] = { mode: 'count', value: prev.value + (hasValue ? 1 : 0) }
        }
      }
    }
    return totals
  }

  // Separate Fetch-Funktionen je Periode
  // userId immer mitschicken: persönliche Summen dürfen bei MANAGER/ADMIN
  // nicht die Zeiten anderer Mitarbeiter enthalten.
  const fetchWeekData = useCallback(async () => {
    setWeekLoading(true)
    try {
      const { week } = getPeriodRanges(weekAnchor)
      const [time, rec, abs, ent] = await Promise.allSettled([
        timeEntriesApi.summary({ dateFrom: week.from, dateTo: week.to, userId: summaryUserId }),
        dailyRecordsApi.list({ dateFrom: week.from, dateTo: week.to, limit: 7, userId: summaryUserId }),
        absencesApi.list({ dateFrom: week.from, dateTo: week.to, userId: summaryUserId, limit: 200 }),
        timeEntriesApi.list({ dateFrom: week.from, dateTo: week.to, userId: summaryUserId, limit: 500 }),
      ])
      setWeekSummary(time.status === 'fulfilled' ? time.value.data : null)
      setWeekDailyRecords(rec.status === 'fulfilled' ? (rec.value.data.data ?? []) : [])
      setWeekAbsences(abs.status === 'fulfilled' ? (abs.value.data.data ?? []) : [])
      setWeekEntries(ent.status === 'fulfilled' ? (ent.value.data.data ?? []) : [])
    } finally {
      setWeekLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO(AP-08): Abhaengigkeiten pruefen; Ergaenzen kann zusaetzliche Laeufe ausloesen
  }, [weekAnchor, summaryUserId])

  const fetchMonthData = useCallback(async () => {
    setMonthLoading(true)
    try {
      const { month } = getPeriodRanges(monthAnchor)
      const [time, rec, abs] = await Promise.allSettled([
        timeEntriesApi.summary({ dateFrom: month.from, dateTo: month.to, userId: summaryUserId }),
        dailyRecordsApi.list({ dateFrom: month.from, dateTo: month.to, limit: 31, userId: summaryUserId }),
        absencesApi.list({ dateFrom: month.from, dateTo: month.to, userId: summaryUserId, limit: 400 }),
      ])
      setMonthSummary(time.status === 'fulfilled' ? time.value.data : null)
      setMonthDailyRecords(rec.status === 'fulfilled' ? (rec.value.data.data ?? []) : [])
      setMonthAbsences(abs.status === 'fulfilled' ? (abs.value.data.data ?? []) : [])
    } finally {
      setMonthLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO(AP-08): Abhaengigkeiten pruefen; Ergaenzen kann zusaetzliche Laeufe ausloesen
  }, [monthAnchor, summaryUserId])

  const fetchYearData = useCallback(async () => {
    setYearLoading(true)
    try {
      const { year } = getPeriodRanges(yearAnchor)
      const [time, rec, abs] = await Promise.allSettled([
        timeEntriesApi.summary({ dateFrom: year.from, dateTo: year.to, userId: summaryUserId }),
        dailyRecordsApi.list({ dateFrom: year.from, dateTo: year.to, limit: 366, userId: summaryUserId }),
        absencesApi.list({ dateFrom: year.from, dateTo: year.to, userId: summaryUserId, limit: 800 }),
      ])
      setYearSummary(time.status === 'fulfilled' ? time.value.data : null)
      setYearDailyRecords(rec.status === 'fulfilled' ? (rec.value.data.data ?? []) : [])
      setYearAbsences(abs.status === 'fulfilled' ? (abs.value.data.data ?? []) : [])
    } finally {
      setYearLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO(AP-08): Abhaengigkeiten pruefen; Ergaenzen kann zusaetzliche Laeufe ausloesen
  }, [yearAnchor, summaryUserId])

  // AP-19: Fehlzeiten des angezeigten Tages (für den gewählten Mitarbeiter)
  const fetchDayAbsences = useCallback(async () => {
    try {
      const { data } = await absencesApi.list({ dateFrom: viewDate, dateTo: viewDate, userId: summaryUserId, limit: 50 })
      setDayAbsences(data.data ?? [])
    } catch {
      setDayAbsences([])
    }
  }, [viewDate, summaryUserId])

  // Fehlzeit-Typen (für den Fehlzeit-Reiter) — einmalig laden
  const fetchAbsenceTypes = useCallback(async () => {
    try {
      const { data } = await absenceTypesApi.list({ isActive: true, limit: 200 })
      setAbsenceTypes(data.data ?? [])
      setFzTypeId((prev) => prev || data.data?.[0]?.id || '')
    } catch {
      setAbsenceTypes([])
    }
  }, [])

  // Zuletzt erfasste Fehlzeiten des gewählten Mitarbeiters (Fehlzeit-Reiter)
  const fetchRecentAbsences = useCallback(async () => {
    try {
      const { data } = await absencesApi.list({ userId: summaryUserId, limit: 30, sortDir: 'desc' })
      setRecentAbsences(data.data ?? [])
    } catch {
      setRecentAbsences([])
    }
  }, [summaryUserId])

  // Fetch Typ-B schema (Diäten-Felder) for label display
  const fetchDailyRecordSchema = useCallback(async () => {
    try {
      const { data } = await timeConfigApi.getCustomFieldsSchema()
      setDailyRecordSchema(Array.isArray(data) ? data : [])
    } catch {
      // schema not configured – ignore
    }
  }, [])

  // Fetch TenantTimeConfig für Edit-Policy + Privacy Consent.
  // clock-settings (nicht das ADMIN-only GET /time-config) — sonst bekommen
  // Nicht-Admin-Bucher 403, der Fehler wird geschluckt und die Maske läuft in
  // den stillen Fallback: Flags wie breakMinutesEnabled/allowNotes/editPolicy
  // fallen auf ihre Defaults zurück (Pausenfeld immer sichtbar, unabhängig von
  // der Admin-Einstellung).
  const fetchConfig = useCallback(async () => {
    try {
      const { data } = await timeConfigApi.getClockSettings()
      const modes = Array.isArray(data.allowedModes) ? data.allowedModes as string[] : ['CLOCK', 'MANUAL']
      setConfigAllowedModes(modes)
      setConfigAllowNotes(data.allowNotes)
      setBreakMinutesEnabled(data.breakMinutesEnabled ?? true)
      setEditPolicy(data.editPolicy)
      setEditWindowHours(data.editWindowHours)
      setPrivacyText(data.privacyText ?? null)
      setPrivacyUrl(data.privacyUrl ?? null)
      setManualHours(data.monthlyReportTimeSource === 'MANUAL')
      setDayFields(Array.isArray(data.monthlyReportDayFields) ? data.monthlyReportDayFields : [])
      // Aktuellen Modus auf ersten erlaubten Modus setzen, falls der aktuelle gesperrt ist
      setMode((prev) => {
        const prevUpper = prev.toUpperCase()
        if (modes.includes(prevUpper)) return prev
        if (modes.includes('CLOCK')) return 'clock'
        if (modes.includes('MANUAL')) return 'manual'
        return prev
      })
    } catch {
      // Fallback: sichere Voreinstellung
    }
  }, [])

  // Fetch active entry
  const fetchActiveEntry = useCallback(async () => {
    try {
      const { data } = await timeEntriesApi.getActive()
      setActiveEntry(data)
    } catch (err: unknown) {
      // 404 means no active entry — that's normal, not an error
      const axiosErr = err as { response?: { status?: number } }
      if (axiosErr?.response?.status === 404) {
        setActiveEntry(null)
      } else {
        console.error('Failed to fetch active entry', err)
      }
    }
  }, [])

  // AP-06-A: Privacy Consent prüfen
  useEffect(() => {
    privacyConsentApi.check().then(({ data }) => {
      // Zustimmung ist Voraussetzung für die erste Zeiterfassung → Modal zeigen,
      // solange nicht zugestimmt wurde. Ist keine Erklärung hinterlegt, zeigt das
      // Modal einen Hinweis (an Admin wenden) statt der Bestätigung.
      setConsentConfigured(data.policyConfigured ?? true)
      if (!data.hasConsented) setConsentRequired(true)
    }).catch(() => {
      // Im Fehlerfall kein Modal anzeigen — verhindert Blockade bei Netzwerkproblemen
    })
  }, [])

  // Mitarbeiterliste für die Zielauswahl (nur MANAGER+ dürfen fremd buchen)
  useEffect(() => {
    if (!isManager) return
    usersApi.list({ limit: 100, kind: 'EMPLOYEE' })
      .then((res) => setEmployees(res.data.data))
      .catch((err) => console.warn('Mitarbeiterliste konnte nicht geladen werden', err))
  }, [isManager])

  // Initial load
  useEffect(() => {
    fetchTimeEntrySchema()
    fetchStandardFieldDefs()
    fetchProjects()
    fetchBetriebsstaetteItems()
    fetchActivityTypes()
    fetchEntries()
    fetchActiveEntry()
    fetchDailyRecord()
    fetchDayAbsences()
    fetchDailyRecordSchema()
    fetchConfig()
    fetchAbsenceTypes()
  }, [fetchTimeEntrySchema, fetchStandardFieldDefs, fetchProjects, fetchBetriebsstaetteItems, fetchActivityTypes, fetchEntries, fetchActiveEntry, fetchDailyRecord, fetchDayAbsences, fetchDailyRecordSchema, fetchConfig, fetchAbsenceTypes])

  useEffect(() => { fetchWeekData() },  [fetchWeekData])
  useEffect(() => { fetchMonthData() }, [fetchMonthData])
  useEffect(() => { fetchYearData() },  [fetchYearData])
  useEffect(() => { fetchRecentAbsences() }, [fetchRecentAbsences])

  // AP-19: Plausiblen Stunden-Vorschlag setzen, solange der Nutzer das Feld nicht
  // selbst geändert hat. Reagiert auf Typwechsel und (neu geladene) Historie des
  // gewählten Mitarbeiters; recentAbsences folgt bereits `summaryUserId`.
  useEffect(() => {
    if (mode !== 'fehlzeit' || fzHoursTouched) return
    const suggestion = suggestAbsenceHours(recentAbsences, fzTypeId)
    if (suggestion !== null) setFzHours(suggestion)
  }, [mode, fzTypeId, recentAbsences, fzHoursTouched])

  // Fehlzeit-Erfolgshinweis nach kurzer Zeit ausblenden
  useEffect(() => {
    if (!fzSuccess) return
    const timer = setTimeout(() => setFzSuccess(false), 2500)
    return () => clearTimeout(timer)
  }, [fzSuccess])

  // Timer logic
  useEffect(() => {
    if (!activeEntry?.startedAt) {
      setElapsedSeconds(0)
      return
    }

    const interval = setInterval(() => {
      const now = new Date().getTime()
      const started = new Date(activeEntry.startedAt!).getTime()
      const elapsed = Math.floor((now - started) / 1000)
      setElapsedSeconds(elapsed)
    }, 1000)

    return () => clearInterval(interval)
  }, [activeEntry?.startedAt])

  // Format time
  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
  }

  // Format ISO datetime or HH:MM to display time.
  // Zeiteinträge werden als "Lokalzeit ohne Offset" gespeichert (Wall-Clock 1:1 als
  // UTC-Stempel) → UTC-Stunden lesen, nicht lokale. Konsistent mit PWA/Backend.
  const formatEntryTime = (time: string | null | undefined): string => {
    if (!time) return '--:--'
    // If already in HH:MM format, return as-is
    if (/^\d{2}:\d{2}$/.test(time)) return time
    const formatted = fmtTimeUTC(time)
    return formatted || '--:--'
  }

  // Format duration — gemäß tenant-weitem Stunden-Format (Dezimal / H:MM)
  const formatDuration = (minutes: number): string => formatHoursWithUnit(minutes, hoursFormat)

  // ── Edit/Delete-Berechtigung prüfen ───────────────────────────────────────
  const canEditEntry = (entry: TimeEntry): boolean => {
    if (editPolicy === 'LOCKED') return false
    if (entry.status === 'SYNCED' || entry.status === 'REVIEWED') return false
    if (editPolicy === 'TIME_WINDOW' && editWindowHours !== null) {
      const entryTime = new Date(entry.startTime)
      const windowEnd = new Date(entryTime.getTime() + editWindowHours * 60 * 60 * 1000)
      // KA-44: Beginn ist Wandzeit — „jetzt" auch, sonst ist das Fenster in der Sommerzeit zwei Stunden zu lang.
      const d = new Date()
      const jetzt = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds()))
      return jetzt <= windowEnd
    }
    return true
  }

  const canDeleteEntry = (entry: TimeEntry): boolean => {
    if (editPolicy === 'LOCKED') return false
    if (editPolicy === 'WITH_NOTE') return false // Löschen nur mit Begründung nicht erlaubt
    // SYNCED und REVIEWED sind gesperrt (bereits extern verarbeitet oder geprüft)
    return entry.status === 'DRAFT' || entry.status === 'SAVED'
  }

  // ── Zeit-Hilfsfunktion: ISO/HH:MM → HH:MM für input[type=time] ───────────
  // UTC-Stunden lesen (Wall-Clock-als-UTC-Konvention), sonst Offset-Verschiebung.
  const toTimeInput = (val: string | null | undefined): string => {
    if (!val) return ''
    if (/^\d{2}:\d{2}$/.test(val)) return val
    return fmtTimeUTC(val)
  }

  // ── Bearbeiten starten ────────────────────────────────────────────────────
  const handleStartEdit = (entry: TimeEntry) => {
    setEditingEntryId(entry.id)
    const sfRaw = (entry.standardFields as Record<string, unknown> | null) ?? {}
    const sfStr: Record<string, string> = {}
    for (const [k, v] of Object.entries(sfRaw)) sfStr[k] = v == null ? '' : String(v)
    setEditForm({
      startTime: toTimeInput(entry.startTime),
      endTime: toTimeInput(entry.endTime),
      breakMinutes: entry.breakMinutes ?? 0,
      note: entry.note ?? '',
      editNote: '',
      projectId: entry.projectId ?? '',
      activityTypeId: entry.activityTypeId ?? '',
      projectRefId: (entry as unknown as { projectRefId?: string }).projectRefId ?? '',
      activityRefId: (entry as unknown as { activityRefId?: string }).activityRefId ?? '',
      // Im Ref-Modus stehen die Positions-/Step-IDs am Eintrag; im Legacy-Modus
      // werden sie nach dem Kaskaden-Laden per Namensabgleich nachgezogen (Effekt unten).
      selectedProjektposition: (entry as unknown as { positionRefId?: string }).positionRefId ?? '',
      selectedArbeitsschritt: (entry as unknown as { stepRefId?: string }).stepRefId ?? '',
      standardFields: sfStr,
      customFields: (entry.customFields as Record<string, unknown>) ?? {},
    })
    setEditError(null)
    setDeletingEntryId(null)
  }

  const handleCancelEdit = () => {
    setEditingEntryId(null)
    setEditError(null)
  }

  const handleSaveEdit = async (entry: TimeEntry) => {
    if (editPolicy === 'WITH_NOTE' && !editForm.editNote.trim()) {
      setEditError('Bitte gib eine Begründung für die Änderung an.')
      return
    }

    // ── Plausibilitätsprüfung 1: Von muss vor Bis liegen ─────────────────────
    const toMinutes = (hhmm: string): number => {
      const [h, m] = hhmm.split(':').map(Number)
      return h * 60 + m
    }
    if (editForm.startTime && editForm.endTime) {
      if (toMinutes(editForm.endTime) < toMinutes(editForm.startTime)) {
        setEditError('Die Endzeit darf nicht vor der Startzeit liegen (z. B. 09:00 – 17:30).')
        return
      }
    }

    // ── Plausibilitätsprüfung 2: Überschneidung mit anderen Einträgen ─────────
    const newStart = toMinutes(editForm.startTime)
    const newEnd = editForm.endTime ? toMinutes(editForm.endTime) : null
    const overlapping = entries.find((e) => {
      if (e.id === entry.id) return false            // eigenen Eintrag überspringen
      const eStart = toMinutes(toTimeInput(e.startTime))
      const eEnd = e.endTime ? toMinutes(toTimeInput(e.endTime)) : null
      if (eStart === 0 && eEnd === null) return false // unvollständiger Eintrag
      // Überschneidung: newStart < eEnd  UND  newEnd > eStart
      const endA = newEnd ?? (newStart + 1)
      const endB = eEnd ?? (eStart + 1)
      return newStart < endB && endA > eStart
    })
    if (overlapping) {
      const von = toTimeInput(overlapping.startTime)
      const bis = overlapping.endTime ? toTimeInput(overlapping.endTime) : '–'
      setEditError(`Die Zeiten überschneiden sich mit einem anderen Eintrag (${von} – ${bis}).`)
      return
    }

    // ISO-Datetime aus Datum (des Eintrags) + eingegebener Uhrzeit bauen.
    // WICHTIG: Die eingegebene Wall-Clock-Zeit wird 1:1 als UTC-Stempel gespeichert —
    // konsistent mit clockIn/clockOut und manuellen Buchungen (Backend legt die
    // Lokalzeit ohne Offset ab, Anzeige liest getUTCHours()). Daher KEIN
    // new Date(...).toISOString(): das würde den lokalen TZ-Offset des Browsers
    // anwenden und z.B. 13:30 (Vienna, UTC+2) fälschlich zu 11:30Z verschieben.
    // Stattdessen die HH:MM direkt mit "Z" als UTC kennzeichnen.
    // entry.date kommt von Prisma als ISO-String z.B. "2026-04-03T00:00:00.000Z"
    // → nur die ersten 10 Zeichen (YYYY-MM-DD) verwenden.
    const dateStr = entry.date.substring(0, 10)
    const buildISO = (time: string): string | undefined =>
      time ? `${dateStr}T${time}:00.000Z` : undefined

    // Standardfelder (Hierarchie-Snapshot) aus der kaskadierten Auswahl neu
    // aufbauen — analog zur Buchungsmaske (V1-Pfad von buildStandardFieldsPayload),
    // aber unabhängig vom bookingMode der Buchungsmaske.
    const hierarchyFieldNames = new Set(['projekt', 'projektnummer', 'idprojekt',
      'projektposition', 'idprojektposition', 'nummerprojektposition',
      'arbeitsschritt', 'idarbeitsschritt', 'taetigkeit'])
    const editSf: Record<string, unknown> = {}
    // Freie Standard-Felder (inkl. Betriebsstätte) aus dem Edit-State übernehmen
    for (const [k, v] of Object.entries(editForm.standardFields)) {
      if (hierarchyFieldNames.has(k)) continue
      if (v !== undefined && v !== null && v !== '') editSf[k] = v
    }
    // Hierarchie-Felder aus den gewählten IDs ableiten (V1)
    const editProjObj = (clockProjectsUI as ProjectLike[]).find(p => p.id === (clockProjectIsRef ? editForm.projectRefId : editForm.projectId))
    if (editProjObj) {
      editSf['projekt']       = editProjObj.name
      editSf['projektnummer'] = editProjObj.projectNumber
      if (editProjObj.externalId) editSf['idprojekt'] = Number(editProjObj.externalId) || editProjObj.externalId
    }
    const editPpObj = editPositionsUI.find(pp => pp.id === editForm.selectedProjektposition)
    if (editPpObj) {
      editSf['projektposition'] = editPpObj.name
      if (editPpObj.nummer != null) editSf['nummerprojektposition'] = editPpObj.nummer
    }
    const editAsObj = editStepsUI.find(a => a.id === editForm.selectedArbeitsschritt)
    if (editAsObj) {
      editSf['arbeitsschritt'] = editAsObj.name
    }

    const payload: UpdateTimeEntryPayload = {
      startTime: buildISO(editForm.startTime),
      endTime: editForm.endTime ? buildISO(editForm.endTime) : undefined,
      breakMinutes: Number(editForm.breakMinutes) || 0,
      ...(configAllowNotes && { note: editForm.note || undefined }),
      // Begründung der Änderung — bei WITH_NOTE verlangt sie der Server.
      ...(editForm.editNote.trim() && { editNote: editForm.editNote.trim() }),
      // Projekt/Position/Arbeitsschritt + Tätigkeit je nach Datenquelle routen —
      // exakt wie buildBookingIds in der Buchungsmaske.
      ...editBookingDS.buildBookingIds({
        legacyProjectId:      clockProjectIsRef  ? null : editForm.projectId,
        legacyActivityTypeId: clockActivityIsRef ? null : editForm.activityTypeId,
        refProjectId:         clockProjectIsRef  ? editForm.projectRefId : null,
        refPositionId:        clockProjectIsRef  ? editForm.selectedProjektposition : null,
        refStepId:            clockProjectIsRef  ? editForm.selectedArbeitsschritt : null,
        refActivityId:        clockActivityIsRef ? editForm.activityRefId : null,
      }),
      // Bei leerer Auswahl die IDs explizit auf null setzen (sonst bleibt der
      // alte Wert stehen, weil buildBookingIds leere IDs weglässt).
      ...(clockProjectIsRef
        ? { projectRefId: editForm.projectRefId || null, positionRefId: editForm.selectedProjektposition || null, stepRefId: editForm.selectedArbeitsschritt || null }
        : { projectId: editForm.projectId || null }),
      ...(clockActivityIsRef
        ? { activityRefId: editForm.activityRefId || null }
        : { activityTypeId: editForm.activityTypeId || null }),
      ...(Object.keys(editSf).length > 0 && { standardFields: editSf }),
      ...(Object.keys(editForm.customFields).length > 0 && { customFields: editForm.customFields }),
    }
    setEditLoading(true)
    setEditError(null)
    try {
      await timeEntriesApi.update(entry.id, payload)
      setEditingEntryId(null)
      fetchEntries()
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } }
      setEditError(apiErrorText(axiosErr, 'Speichern fehlgeschlagen. Bitte erneut versuchen.'))
    } finally {
      setEditLoading(false)
    }
  }

  // ── Löschen ───────────────────────────────────────────────────────────────
  const handleDeleteEntry = async (id: string) => {
    setDeleteLoading(true)
    try {
      await timeEntriesApi.delete(id)
      setDeletingEntryId(null)
      await fetchEntries()
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } }
      setError(apiErrorText(axiosErr, 'Löschen fehlgeschlagen. Bitte erneut versuchen.'))
      setDeletingEntryId(null)
    } finally {
      setDeleteLoading(false)
    }
  }

  // Translate status to German
  const translateStatus = (status: string): string => {
    switch (status) {
      case 'DRAFT': return t('timeTracking.draft', { defaultValue: 'Entwurf' })
      case 'SAVED': return t('timeTracking.saved', { defaultValue: 'Gespeichert' })
      case 'SYNCED': return t('timeTracking.synced', { defaultValue: 'Synchronisiert' })
      case 'REVIEWED': return t('timeTracking.reviewed', { defaultValue: 'Geprüft' })
      default: return status
    }
  }

  // Get today's date
  const getTodayDate = (): string => {
    return new Date().toLocaleDateString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  // Clock in
  const handleClockIn = async () => {
    if (!clockInRequiredFilled) return
    setClockLoading(true)
    try {
      // Lokale Wall-Clock-Zeit mitschicken — sonst speichert das Backend die echte
      // UTC-Serverzeit (z.B. 11:27 statt 13:27 in CEST). Konsistent mit der PWA.
      const clockInNow = new Date()
      const payload: ClockInPayload = {
        clientDate: toLocalDateStr(clockInNow),
        clientTime: toLocalTimeStr(clockInNow),
      }
      // AP-09-E: buildBookingIds routet je nach DataSourceMode entweder auf
      // Legacy-IDs (projectId/activityTypeId) oder auf Ref-IDs (projectRefId,
      // positionRefId, stepRefId, activityRefId).
      Object.assign(payload, clockBookingDS.buildBookingIds({
        legacyProjectId:       clockProjectIsRef  ? null : clockSelectedProject,
        legacyActivityTypeId:  clockActivityIsRef ? null : clockSelectedActivity,
        refProjectId:          clockProjectIsRef  ? clockSelectedProject : null,
        refPositionId:         clockProjectIsRef  ? clockSelectedProjektposition : null,
        refStepId:             clockProjectIsRef  ? clockSelectedArbeitsschritt : null,
        refActivityId:         clockActivityIsRef ? clockSelectedActivity : null,
      }))
      if (clockNote) payload.note = clockNote
      if (Object.keys(clockCustomFields).length > 0) payload.customFields = clockCustomFields
      // Build standardFields payload (Hierarchie-bewusst)
      if (standardFieldDefs.length > 0) {
        const sf = buildStandardFieldsPayload(
          clockStandardFields, clockSelectedProject, clockSelectedProjektposition,
          clockSelectedArbeitsschritt, clockProjektpositionen, clockArbeitsschritte, clockV2Result
        )
        if (Object.keys(sf).length > 0) payload.standardFields = sf
      }

      const { data } = await timeEntriesApi.clockIn(payload)
      setActiveEntry(data)
      setClockSelectedProject('')
      setClockSelectedActivity('')
      setClockNote('')
      setBreakMinutes(0)
      setClockSelectedProjektposition('')
      setClockSelectedArbeitsschritt('')
      setClockProjektpositionen([])
      setClockArbeitsschritte([])
      setClockV2Id('')
      setClockV2Result(null)
      setClockV2Error(null)
      // Reset custom fields to empty
      const reset: Record<string, unknown> = {}
      timeEntrySchema.forEach((f) => { reset[f.name] = f.type === 'checkbox' ? false : '' })
      setClockCustomFields(reset)
      // Reset standard fields to empty
      const sfReset: Record<string, unknown> = {}
      standardFieldDefs.forEach((f) => { sfReset[f.name] = '' })
      setClockStandardFields(sfReset)
      setError(null)

      // Refresh entries list
      await fetchEntries()
    } catch (err) {
      console.error('Failed to clock in', err)
      setError(apiErrorText(err, t('timeTracking.errorClockIn', { defaultValue: 'Einstempeln fehlgeschlagen. Bitte erneut versuchen.' })))
    } finally {
      setClockLoading(false)
    }
  }

  // Clock out
  const handleClockOut = async () => {
    if (!activeEntry?.id) return

    setClockLoading(true)
    try {
      // Lokale Wall-Clock-Endzeit mitschicken (siehe handleClockIn) — sonst
      // landet die echte UTC-Serverzeit als Endzeit (z.B. 11:30 statt 13:30).
      const clockOutNow = new Date()
      const payload: ClockOutPayload = {
        clientDate: toLocalDateStr(clockOutNow),
        clientTime: toLocalTimeStr(clockOutNow),
      }
      if (breakMinutes > 0) payload.breakMinutes = breakMinutes
      if (clockNote) payload.note = clockNote

      await timeEntriesApi.clockOut(activeEntry.id, payload)
      setActiveEntry(null)
      setElapsedSeconds(0)
      setBreakMinutes(0)
      setClockNote('')
      setError(null)

      // Refresh entries list
      await fetchEntries()
    } catch (err) {
      console.error('Failed to clock out', err)
      setError(t('timeTracking.errorClockOut', { defaultValue: 'Ausstempeln fehlgeschlagen. Bitte erneut versuchen.' }))
    } finally {
      setClockLoading(false)
    }
  }

  // Create manual entry
  const handleCreateManualEntry = async () => {
    if (!manualRequiredFilled) return

    // ── Plausibilitätsprüfung 1: Von muss vor Bis liegen ─────────────────────
    const toMins = (hhmm: string): number => {
      const [h, m] = hhmm.split(':').map(Number)
      return h * 60 + m
    }
    if (manualStartTime && manualEndTime) {
      if (toMins(manualEndTime) <= toMins(manualStartTime)) {
        setManualError('Die Endzeit muss nach der Startzeit liegen (z. B. 09:00 – 17:30).')
        return
      }
    }

    // ── Plausibilitätsprüfung 2: Überschneidung mit bestehenden Einträgen ────
    const today = new Date().toISOString().split('T')[0]
    let dateEntries: TimeEntry[] = []
    if (manualDate === today) {
      // Bereits im State vorhanden
      dateEntries = entries
    } else {
      // Für andere Daten gezielt laden — Überschneidungsprüfung gegen die
      // Buchungen des Zielmitarbeiters (bei Fremdbuchung dessen, sonst eigene).
      try {
        const { data } = await timeEntriesApi.list({ dateFrom: manualDate, dateTo: manualDate, userId: summaryUserId })
        dateEntries = data.data
      } catch {
        // Prüfung nicht möglich – trotzdem weitermachen, Backend validiert
        dateEntries = []
      }
    }
    const newStart = toMins(manualStartTime)
    const newEnd = manualEndTime ? toMins(manualEndTime) : null
    const overlapping = dateEntries.find((e) => {
      const eStart = toMins(toTimeInput(e.startTime))
      const eEnd = e.endTime ? toMins(toTimeInput(e.endTime)) : null
      const endA = newEnd ?? (newStart + 1)
      const endB = eEnd ?? (eStart + 1)
      return newStart < endB && endA > eStart
    })
    if (overlapping) {
      const von = toTimeInput(overlapping.startTime)
      const bis = overlapping.endTime ? toTimeInput(overlapping.endTime) : '–'
      setManualError(`Die Zeiten überschneiden sich mit einem bestehenden Eintrag (${von} – ${bis}).`)
      return
    }

    setManualError(null)
    setManualLoading(true)
    try {
      const payload: CreateTimeEntryPayload = {
        date: manualDate,
        startTime: manualStartTime,
        endTime: manualEndTime,
      }
      // MANAGER+ können die Buchung einem anderen Mitarbeiter zuordnen.
      // Leer ('') → Backend bucht für den angemeldeten Nutzer.
      if (isManager && selectedEmployeeId) payload.userId = selectedEmployeeId
      // AP-09-E: buildBookingIds wie oben (Legacy- vs. Ref-IDs je nach Modus)
      Object.assign(payload, manualBookingDS.buildBookingIds({
        legacyProjectId:       manualProjectIsRef  ? null : manualSelectedProject,
        legacyActivityTypeId:  manualActivityIsRef ? null : manualSelectedActivity,
        refProjectId:          manualProjectIsRef  ? manualSelectedProject : null,
        refPositionId:         manualProjectIsRef  ? manualSelectedProjektposition : null,
        refStepId:             manualProjectIsRef  ? manualSelectedArbeitsschritt : null,
        refActivityId:         manualActivityIsRef ? manualSelectedActivity : null,
      }))
      if (manualBreak > 0) payload.breakMinutes = manualBreak
      if (manualNote) payload.note = manualNote

      if (Object.keys(manualCustomFields).length > 0) payload.customFields = manualCustomFields
      // Build standardFields payload (Hierarchie-bewusst)
      if (standardFieldDefs.length > 0) {
        const sf = buildStandardFieldsPayload(
          manualStandardFields, manualSelectedProject, manualSelectedProjektposition,
          manualSelectedArbeitsschritt, manualProjektpositionen, manualArbeitsschritte, manualV2Result
        )
        if (Object.keys(sf).length > 0) payload.standardFields = sf
      }

      await timeEntriesApi.create(payload)

      // Reset form
      setManualDate(new Date().toISOString().split('T')[0])
      setManualStartTime(defaultManualStart)
      setManualEndTime(defaultManualEnd)
      setManualSelectedProject('')
      setManualSelectedActivity('')
      setManualBreak(0)
      setManualNote('')
      setManualSelectedProjektposition('')
      setManualSelectedArbeitsschritt('')
      setManualProjektpositionen([])
      setManualArbeitsschritte([])
      setManualV2Id('')
      setManualV2Result(null)
      setManualV2Error(null)
      // Reset custom fields to empty
      const reset: Record<string, unknown> = {}
      timeEntrySchema.forEach((f) => { reset[f.name] = f.type === 'checkbox' ? false : '' })
      setManualCustomFields(reset)
      // Reset standard fields to empty
      const sfReset: Record<string, unknown> = {}
      standardFieldDefs.forEach((f) => { sfReset[f.name] = '' })
      setManualStandardFields(sfReset)
      setError(null)
      setManualError(null)

      // Refresh entries list
      await fetchEntries()
    } catch (err) {
      console.error('Failed to create manual entry', err)
      setManualError(apiErrorText(err, t('timeTracking.errorCreatingEntry', { defaultValue: 'Zeiteintrag konnte nicht erstellt werden. Bitte Eingaben prüfen.' })))
    } finally {
      setManualLoading(false)
    }
  }

  // Render a single Typ-A custom field (shared for clock and manual mode)
  const renderCustomField = (
    field: CustomFieldSchema,
    values: Record<string, unknown>,
    onChange: (name: string, value: unknown) => void,
    idPrefix: string,
  ) => {
    const value = values[field.name] ?? (field.type === 'checkbox' ? false : '')
    const fieldId = `${idPrefix}-${field.name}`

    // Wenn Optionen hinterlegt sind, immer als Dropdown rendern (außer Checkbox)
    if (field.options && field.options.length > 0 && field.type !== 'checkbox') {
      return (
        <div key={field.name} className="space-y-2">
          <label htmlFor={fieldId} className="block text-xs font-semibold uppercase tracking-wide text-primary pl-3">
            {field.label}
            {field.unit && <span className="ml-1 text-xs text-muted-foreground">({field.unit})</span>}
            {field.required && <span className="text-destructive"> *</span>}
          </label>
          <SearchSelect
            id={fieldId}
            value={value as string}
            onChange={(v) => onChange(field.name, v)}
            options={field.options.map((opt) => ({ value: opt, label: opt }))}
            placeholder="Wählen…"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      )
    }

    switch (field.type) {
      case 'text':
        return (
          <div key={field.name} className="space-y-2">
            <label htmlFor={fieldId} className="block text-xs font-semibold uppercase tracking-wide text-primary pl-3">
              {field.label}{field.required && <span className="text-destructive"> *</span>}
            </label>
            <input
              id={fieldId}
              type="text"
              value={value as string}
              onChange={(e) => onChange(field.name, e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        )
      case 'number':
        return (
          <div key={field.name} className="space-y-2">
            <label htmlFor={fieldId} className="block text-xs font-semibold uppercase tracking-wide text-primary pl-3">
              {field.label}{field.unit && <span className="ml-1 text-xs text-muted-foreground">({field.unit})</span>}{field.required && <span className="text-destructive"> *</span>}
            </label>
            <input
              id={fieldId}
              type="number"
              value={value as string}
              onChange={(e) => onChange(field.name, e.target.value ? parseFloat(e.target.value) : '')}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        )
      case 'select':
        return (
          <div key={field.name} className="space-y-2">
            <label htmlFor={fieldId} className="block text-xs font-semibold uppercase tracking-wide text-primary pl-3">
              {field.label}{field.required && <span className="text-destructive"> *</span>}
            </label>
            <SearchSelect
              id={fieldId}
              value={value as string}
              onChange={(v) => onChange(field.name, v)}
              options={(field.options ?? []).map((opt) => ({ value: opt, label: opt }))}
              placeholder="Wählen…"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        )
      case 'time':
        return (
          <div key={field.name} className="space-y-2">
            <label htmlFor={fieldId} className="block text-xs font-semibold uppercase tracking-wide text-primary pl-3">
              {field.label}{field.required && <span className="text-destructive"> *</span>}
            </label>
            <input
              id={fieldId}
              type="time"
              value={value as string}
              onChange={(e) => onChange(field.name, e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        )
      case 'checkbox':
        return (
          <div key={field.name} className="flex items-center gap-3">
            <input
              id={fieldId}
              type="checkbox"
              checked={value === true || value === 'true'}
              onChange={(e) => onChange(field.name, e.target.checked)}
              className="h-4 w-4 rounded border-input text-primary focus:ring-2 focus:ring-ring"
            />
            <label htmlFor={fieldId} className="text-sm font-medium text-foreground cursor-pointer">
              {field.label}{field.required && <span className="text-destructive"> *</span>}
            </label>
          </div>
        )
      default:
        return null
    }
  }

  // ── Gruppen-bewusstes Rendering der Typ-A Felder ──────────────────────────
  // Felder mit gleichem group-Namen werden als Block mit Datensatz-Selektor gerendert.
  // Wird ein Vorgabedatensatz gewählt, werden alle Felder der Gruppe automatisch befüllt.
  const renderCustomFieldsSection = (
    fields: CustomFieldSchema[],
    values: Record<string, unknown>,
    onChange: (name: string, value: unknown) => void,
    idPrefix: string,
  ) => {
    const processedGroups = new Set<string>()
    const elements: JSX.Element[] = []

    for (const field of fields) {
      if (field.group) {
        if (processedGroups.has(field.group)) continue  // Bereits als Teil des Blocks gerendert
        processedGroups.add(field.group)

        const groupFields = fields.filter((f) => f.group === field.group)
        const records = groupRecords[field.group] ?? []

        elements.push(
          <div key={`group-${field.group}`} className="space-y-3 rounded-md border border-border/60 bg-muted/20 p-3">
            {/* Gruppenname als Label */}
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {field.group}
            </p>

            {/* Datensatz-Selektor – nur wenn Vorgabedaten vorhanden */}
            {records.length > 0 && (
              <div className="space-y-1">
                <label className="block text-xs text-muted-foreground">Vorlage wählen (befüllt alle Felder automatisch)</label>
                <SearchSelect
                  value=""
                  onChange={(v) => {
                    const record = records.find((r) => r.id === v)
                    if (record) {
                      groupFields.forEach((gf) => {
                        if (record.values[gf.name] !== undefined) {
                          onChange(gf.name, record.values[gf.name])
                        }
                      })
                    }
                  }}
                  options={records.map((rec) => ({
                    value: rec.id,
                    label: groupFields.map((gf) => rec.values[gf.name]).filter(Boolean).join(' · ') || rec.id,
                  }))}
                  placeholder="– Vorlage auswählen –"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            )}

            {/* Einzelne Felder der Gruppe */}
            {groupFields.map((gf) => renderCustomField(gf, values, onChange, idPrefix))}
          </div>
        )
      } else {
        // Nicht-gruppiertes Feld – normal rendern
        const el = renderCustomField(field, values, onChange, idPrefix)
        if (el) elements.push(el)
      }
    }

    return elements
  }

  // Handle daily record saved
  const handleDailyRecordSaved = () => {
    fetchEntries()
    fetchDailyRecord()
    fetchWeekData()
    fetchMonthData()
    fetchYearData()
  }

  // ── Fehlzeit-Reiter: speichern / löschen ─────────────────────────────────────
  const handleFehlzeitSave = async () => {
    const hoursNum = parseFloat(fzHours.replace(',', '.'))
    if (!fzTypeId) { setFzError('Bitte einen Fehlzeit-Typ wählen.'); return }
    if (!fzDate) { setFzError('Bitte ein Datum wählen.'); return }
    if (!Number.isFinite(hoursNum) || hoursNum <= 0) { setFzError('Bitte eine gültige Stundenzahl eingeben.'); return }
    setFzSaving(true)
    setFzError(null)
    try {
      await absencesApi.create({
        // MANAGER+ können die Fehlzeit einem anderen Mitarbeiter zuordnen.
        ...(targetEmployeeId ? { userId: targetEmployeeId } : {}),
        absenceTypeId: fzTypeId,
        date: fzDate,
        durationMinutes: Math.round(hoursNum * 60),
        note: fzNote.trim() || undefined,
      })
      setFzNote('')
      setFzSuccess(true)
      // Nächste Erfassung darf wieder einen Vorschlag bekommen.
      setFzHoursTouched(false)
      // Fehlzeit fließt in Tages-/Perioden-Zusammenfassungen ein → neu laden
      fetchRecentAbsences()
      fetchDayAbsences()
      fetchWeekData()
      fetchMonthData()
      fetchYearData()
    } catch (err) {
      console.error('Fehlzeit konnte nicht gespeichert werden', err)
      setFzError(apiErrorText(err, 'Fehlzeit konnte nicht gespeichert werden.'))
    } finally {
      setFzSaving(false)
    }
  }

  const handleDeleteAbsence = async (a: Absence) => {
    if (!(await bestaetige('Diese Fehlzeit wirklich löschen?'))) return
    try {
      await absencesApi.delete(a.id)
      fetchRecentAbsences()
      fetchDayAbsences()
      fetchWeekData()
      fetchMonthData()
      fetchYearData()
    } catch (err) {
      console.error('Fehlzeit konnte nicht gelöscht werden', err)
      setFzError(apiErrorText(err, 'Fehlzeit konnte nicht gelöscht werden.'))
    }
  }

  // ── V2-Lookup Handler ─────────────────────────────────────────────────────
  const handleV2Lookup = async (idValue: string, forMode: 'clock' | 'manual') => {
    if (!idValue.trim()) return
    const setLoading = forMode === 'clock' ? setClockV2Loading : setManualV2Loading
    const setError   = forMode === 'clock' ? setClockV2Error   : setManualV2Error
    const setResult  = forMode === 'clock' ? setClockV2Result  : setManualV2Result

    setLoading(true)
    setError(null)
    setResult(null)
    try {
      const { data } = await timeConfigApi.lookupArbeitsschrittByExternalId(idValue.trim())
      if (!data) {
        setError(`IDArbeitsschritt "${idValue}" nicht gefunden.`)
      } else {
        setResult(data)
        // Ref-Treffer: alle drei Ref-Dropdowns setzen, damit die Buchung verknüpft wird
        if (data.source === 'ref' && data.stepRefId) {
          const setProj = forMode === 'clock' ? setClockSelectedProject : setManualSelectedProject
          const setPP   = forMode === 'clock' ? setClockSelectedProjektposition : setManualSelectedProjektposition
          const setAS   = forMode === 'clock' ? setClockSelectedArbeitsschritt : setManualSelectedArbeitsschritt
          setProj(data.projectRefId ?? '')
          setPP(data.positionRefId ?? '')
          setAS(data.stepRefId)
        } else if (data.parent?.project) {
          // Legacy: nur Projekt automatisch setzen (falls gefunden)
          const proj = data.parent.project
          if (forMode === 'clock') {
            setClockSelectedProject(proj.id)
          } else {
            setManualSelectedProject(proj.id)
          }
        }
      }
    } catch {
      setError('Lookup fehlgeschlagen. Bitte erneut versuchen.')
    } finally {
      setLoading(false)
    }
  }

  // ── Standard-Felder Payload bauen ────────────────────────────────────────
  // Baut das standardFields-Objekt aus den gewählten Werten (V1 und V2)
  const buildStandardFieldsPayload = (
    sfState: Record<string, unknown>,
    selectedProjectId: string,
    selectedProjektpositionId: string,
    selectedArbeitsschrittId: string,
    projektpositionen: StandardSelectionItem[],
    arbeitsschritte: StandardSelectionItem[],
    v2Result: ArbeitsschrittLookupResult | null,
  ): Record<string, unknown> => {
    const sf: Record<string, unknown> = {}

    // Freie Standard-Felder (nicht Hierarchie-Felder)
    const hierarchyFieldNames = new Set(['projekt', 'projektnummer', 'idprojekt',
      'projektposition', 'idprojektposition', 'nummerprojektposition',
      'arbeitsschritt', 'idarbeitsschritt', 'taetigkeit'])

    standardFieldDefs
      .filter(f => !f.isId && !f.isRelation && f.visible && !hierarchyFieldNames.has(f.name))
      .forEach(f => {
        const v = sfState[f.name]
        if (v !== undefined && v !== null && v !== '') {
          sf[f.name] = f.type === 'number' && v !== '' ? Number(v) : v
        }
      })

    if (bookingMode === 'v2' && v2Result) {
      // V2: Daten aus dem Lookup-Ergebnis
      sf['arbeitsschritt']    = v2Result.name
      if (v2Result.externalId) sf['idarbeitsschritt'] = Number(v2Result.externalId) || v2Result.externalId
      if (v2Result.parent) {
        sf['projektposition'] = v2Result.parent.name
        if (v2Result.parent.externalId) sf['idprojektposition'] = Number(v2Result.parent.externalId) || v2Result.parent.externalId
        if (v2Result.parent.nummer != null) sf['nummerprojektposition'] = v2Result.parent.nummer
        if (v2Result.parent.project) {
          sf['projektnummer'] = v2Result.parent.project.projectNumber
          if (v2Result.parent.project.externalId) sf['idprojekt'] = Number(v2Result.parent.project.externalId) || v2Result.parent.project.externalId
        }
      }
    } else {
      // V1: Daten aus der kaskadierten Auswahl – vollständig speichern
      const projObj = projects.find(p => p.id === selectedProjectId)
      if (projObj) {
        sf['projekt']       = projObj.name
        sf['projektnummer'] = projObj.projectNumber
        if (projObj.externalId) sf['idprojekt'] = Number(projObj.externalId) || projObj.externalId
      }

      const ppObj = projektpositionen.find(pp => pp.id === selectedProjektpositionId)
      if (ppObj) {
        sf['projektposition'] = ppObj.name
        if (ppObj.nummer != null) sf['nummerprojektposition'] = ppObj.nummer
        if (ppObj.externalId) sf['idprojektposition'] = Number(ppObj.externalId) || ppObj.externalId
      }

      const asObj = arbeitsschritte.find(a => a.id === selectedArbeitsschrittId)
      if (asObj) {
        sf['arbeitsschritt'] = asObj.name
        if (asObj.externalId) sf['idarbeitsschritt'] = Number(asObj.externalId) || asObj.externalId
      }
    }

    return sf
  }

  // ── Standard field visibility helpers ────────────────────────────────────────
  const isFieldVisible = (name: string) => {
    const def = standardFieldDefs.find((f) => f.name === name)
    return def ? def.visible : false
  }
  const isFieldRequired = (name: string) => {
    const def = standardFieldDefs.find((f) => f.name === name)
    return def ? def.required : false
  }

  // ── Validation helpers ─────────────────────────────────────────────────────
  const requiredCustomFields = timeEntrySchema.filter((f) => f.required)
  const requiredStandardFields = standardFieldDefs.filter((f) => !f.isId && !f.isRelation && f.visible && f.required)

  // Relation fields (projekt/taetigkeit): required check on their select values
  const projektRequired = isFieldRequired('projekt')
  const taetigkeitRequired = isFieldRequired('taetigkeit')

  const clockInRequiredFilled =
    requiredCustomFields.every((f) => {
      const v = clockCustomFields[f.name]
      if (f.type === 'checkbox') return v === true
      return v !== undefined && v !== null && v !== ''
    }) &&
    requiredStandardFields.every((f) => {
      const v = clockStandardFields[f.name]
      return v !== undefined && v !== null && v !== ''
    }) &&
    (!projektRequired || !!clockSelectedProject) &&
    (!taetigkeitRequired || !!clockSelectedActivity)

  const manualRequiredFilled =
    requiredCustomFields.every((f) => {
      const v = manualCustomFields[f.name]
      if (f.type === 'checkbox') return v === true
      return v !== undefined && v !== null && v !== ''
    }) &&
    requiredStandardFields.every((f) => {
      const v = manualStandardFields[f.name]
      return v !== undefined && v !== null && v !== ''
    }) &&
    (!projektRequired || !!manualSelectedProject) &&
    (!taetigkeitRequired || !!manualSelectedActivity)

  if (!user) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-muted-foreground">{t('common.loading')}</p>
      </div>
    )
  }

  // AP-06-A: Zeiterfassung bleibt gesperrt, bis die Einwilligung erteilt ist.
  // „Schließen"/„Abbrechen" schließt nur das Modal — die Maske wird NICHT freigegeben.
  if (consentRequired) {
    return (
      <>
        {!consentModalDismissed && (
          <PrivacyConsentModal
            privacyText={privacyText}
            privacyUrl={privacyUrl}
            policyConfigured={consentConfigured}
            onConsent={() => { setConsentRequired(false); setConsentModalDismissed(false) }}
            onCancel={() => setConsentModalDismissed(true)}
          />
        )}
        <div className="p-4 sm:p-6">
          <div className="mx-auto mt-8 max-w-md rounded-lg border border-border bg-card p-6 text-center shadow-sm">
            <h2 className="mb-2 text-lg font-semibold text-foreground">
              {t('timeTracking.consentLockedTitle', { defaultValue: 'Zeiterfassung gesperrt' })}
            </h2>
            <p className="mb-4 text-sm text-muted-foreground">
              {consentConfigured
                ? t('timeTracking.consentLockedBody', { defaultValue: 'Bitte stimme zuerst der Datenschutzerklärung zu, um die Zeiterfassung zu nutzen.' })
                : t('timeTracking.consentLockedNoPolicy', { defaultValue: 'Es ist keine Datenschutzerklärung hinterlegt. Bitte wende dich an deinen Administrator — danach kannst du zustimmen und die Zeiterfassung nutzen.' })}
            </p>
            <Button onClick={() => setConsentModalDismissed(false)}>
              {t('timeTracking.consentShow', { defaultValue: 'Einwilligung anzeigen' })}
            </Button>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
    <div className={pageWide}>
      {/* Header */}
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            {t('timeTracking.title', { defaultValue: 'Zeiterfassung' })}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">{getTodayDate()}</p>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Nur-Lese-Hinweis für READONLY */}
      {isReadonly && (
        <div className="rounded-md border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
          {t('timeTracking.readonlyHint', { defaultValue: 'Nur-Lese-Zugriff — Buchungen sind nicht möglich.' })}
        </div>
      )}

      {/* Mitarbeiter-Auswahl (nur MANAGER+): kompakt, über den Reitern. Gilt für
          alle Erfassungsreiter außer der Stempeluhr. Bei Fremdbuchung (anderer
          Mitarbeiter) warnend eingefärbt, damit es auffällt. */}
      {!isReadonly && isManager && mode !== 'clock' && (
        <div
          className={`flex items-center gap-2.5 rounded-lg border px-3 py-2 ${
            isForeignBooking ? 'border-primary/60 bg-primary/5' : 'border-border'
          }`}
        >
          <div
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
              isForeignBooking ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
            }`}
          >
            {isForeignBooking && selectedEmployee
              ? initialsOf(fmtEmployeeName(selectedEmployee))
              : <UserIcon className="h-4 w-4" aria-hidden="true" />}
          </div>
          <span className={`shrink-0 text-xs ${isForeignBooking ? 'text-primary' : 'text-muted-foreground'}`}>
            {t('timeTracking.bookingFor', { defaultValue: 'Buchung für' })}
          </span>
          <div className="ml-auto min-w-0 flex-1">
            <SearchSelect
              value={selectedEmployeeId}
              onChange={setSelectedEmployeeId}
              options={[
                { value: '', label: t('timeTracking.employeeSelf', { defaultValue: 'Ich selbst' }) },
                ...employees.map((u) => ({ value: u.id, label: fmtEmployeeName(u) })),
              ]}
              className="w-full bg-transparent text-right text-sm font-medium text-foreground focus:outline-none"
              aria-label={t('timeTracking.employee', { defaultValue: 'Mitarbeiter' })}
            />
          </div>
        </div>
      )}
      {!isReadonly && isManager && mode === 'clock' && (
        <div className="flex items-center gap-2 px-1 text-sm text-muted-foreground">
          <Info className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{t('timeTracking.clockSelfOnly', { defaultValue: 'Stempeluhr – immer nur für dich selbst' })}</span>
        </div>
      )}

      {/* Reiter: Stempeluhr | Manuell | Diäten | Fehlzeiten ────────────────────
             Alle nebeneinander wenn Platz reicht, sonst 2×2 (Container-Query
             .mode-tabs / .mode-tabs-grid in index.css). */}
      {!isReadonly && (
        <div className="mode-tabs">
          <div className="mode-tabs-grid rounded-xl border border-border bg-muted/40 p-1.5">
            {configAllowedModes.includes('CLOCK') && (
              <button onClick={() => switchMode('clock')} className={tabCls('clock')}>
                <Clock className="h-5 w-5" aria-hidden="true" />
                <span className="text-[11px] font-medium leading-tight">{t('timeTracking.clock', { defaultValue: 'Stempeluhr' })}</span>
              </button>
            )}
            {configAllowedModes.includes('MANUAL') && (
              <button onClick={() => switchMode('manual')} className={tabCls('manual')}>
                <Pencil className="h-5 w-5" aria-hidden="true" />
                <span className="text-[11px] font-medium leading-tight">{t('timeTracking.manualShort', { defaultValue: 'Manuell' })}</span>
              </button>
            )}
            {/* Der Reiter erscheint, sobald es dort etwas zu erfassen gibt:
                Typ-B-Felder (Diäten), händische Stunden (AP-24) ODER
                eingeschaltete Uhrzeiten-/Montagefelder (AP-26). Vorher hing er
                allein an den Typ-B-Feldern — ein Mandant mit händischer
                Erfassung, aber ohne Diäten, kam nie an seine Stunden. */}
            {(dailyRecordSchema.length > 0 || manualHours || dayFields.length > 0) && (
              <button onClick={() => switchMode('diaeten')} className={tabCls('diaeten')}>
                <Receipt className="h-5 w-5" aria-hidden="true" />
                {/* Namensraum `timeTracking`, nicht `time-tracking` — mit dem
                    Bindestrich griff die Übersetzung nie und der defaultValue
                    „Diäten" blieb stehen, obwohl „Tagesdaten" hinterlegt ist. */}
                <span className="text-[11px] font-medium leading-tight">{t('timeTracking.dailyRecord', { defaultValue: 'Tagesdaten' })}</span>
              </button>
            )}
            <button onClick={() => switchMode('fehlzeit')} className={tabCls('fehlzeit')}>
              <CalendarOff className="h-5 w-5" aria-hidden="true" />
              <span className="text-[11px] font-medium leading-tight">{t('timeTracking.absences', { defaultValue: 'Fehlzeiten' })}</span>
            </button>
          </div>
        </div>
      )}

      {/* Clock Mode */}
      {!isReadonly && mode === 'clock' && (
        <div className="space-y-6">
          {/* Timer Display */}
          <div className="rounded-lg border border-primary/30 bg-muted/20 p-8 text-center">
            {activeEntry ? (
              <div className="space-y-6">
                <div className="py-4">
                  <p className="mb-2 text-sm text-muted-foreground">
                    {t('timeTracking.clockedIn', { defaultValue: 'Gestempelt' })}
                  </p>
                  <p className="text-6xl font-bold text-primary font-mono">
                    {formatTime(elapsedSeconds)}
                  </p>
                  {/* KA-43: wie die Tagesliste — Stammdaten (Ref) vor der alten Tabelle. */}
                  {(activeEntry.projectRef ?? activeEntry.project) && (
                    <p className="mt-4 text-sm text-foreground">
                      {terms.project}: {projectLabel(activeEntry.projectRef?.projectNumber ?? activeEntry.project?.projectNumber, (activeEntry.projectRef ?? activeEntry.project)!.name)}
                    </p>
                  )}
                  {(activeEntry.activityRef ?? activeEntry.activityType) && (
                    <p className="mt-2 text-sm text-foreground">
                      {t('timeTracking.activity', { defaultValue: 'Aktivität' })}: {(activeEntry.activityRef ?? activeEntry.activityType)!.name}
                    </p>
                  )}
                </div>

                {/* Break input – nur wenn der Admin Pausenminuten aktiviert hat */}
                {breakMinutesEnabled && (
                <div className="space-y-2">
                  <label htmlFor="breakInput" className="block text-xs font-semibold uppercase tracking-wide text-primary pl-3">
                    {t('timeTracking.breakMinutes', { defaultValue: 'Pausenminuten' })}
                  </label>
                  <input
                    id="breakInput"
                    type="number"
                    min="0"
                    value={breakMinutes}
                    onChange={(e) => setBreakMinutes(Math.max(0, parseInt(e.target.value) || 0))}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-center text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                )}

                {/* Note – nur wenn Notizen in den Einstellungen erlaubt */}
                {configAllowNotes && (
                  <div className="space-y-2 text-left">
                    <label htmlFor="clockNoteActive" className="block text-xs font-semibold uppercase tracking-wide text-primary pl-3">
                      {t('timeTracking.note', { defaultValue: 'Notiz' })}                    </label>
                    <textarea
                      id="clockNoteActive"
                      value={clockNote}
                      onChange={(e) => setClockNote(e.target.value)}
                      placeholder={t('timeTracking.notePlaceholder', { defaultValue: 'Optionale Notiz...' })}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      rows={2}
                    />
                  </div>
                )}

                {/* Clock out button */}
                <Button
                  onClick={handleClockOut}
                  disabled={clockLoading}
                  className="w-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  size="lg"
                >
                  {clockLoading
                    ? t('common.loading')
                    : t('timeTracking.clockOut', { defaultValue: 'Ausstempeln' })}
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="py-4">
                  <p className="mb-2 text-sm text-muted-foreground">
                    {t('timeTracking.notClockedIn', { defaultValue: 'Nicht gestempelt' })}
                  </p>
                  <p className="text-6xl font-bold text-muted-foreground font-mono">00:00:00</p>
                </div>

                {/* Clock in form */}
                <div className="space-y-4 text-left">
                  {/* Pflichtfelder: freie Standard-Felder (ohne Hierarchie-Felder) */}
                  {standardFieldDefs
                    .filter((f) => !f.isId && !f.isRelation && f.visible && f.required
                      && !['projektposition', 'arbeitsschritt', 'nummerprojektposition', 'betriebsstaette'].includes(f.name))
                    .map((field) => (
                    <div key={field.name} className="space-y-2">
                      <label htmlFor={`clock-sf-${field.name}`} className="block text-xs font-semibold uppercase tracking-wide text-primary pl-3">
                        {field.label}<span className="text-destructive"> *</span>
                      </label>
                      <input
                        id={`clock-sf-${field.name}`}
                        type={field.type === 'number' ? 'number' : 'text'}
                        value={(clockStandardFields[field.name] ?? '') as string}
                        onChange={(e) => setClockStandardFields((prev) => ({ ...prev, [field.name]: e.target.value }))}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                  ))}
                  {/* Betriebsstätte – Pflichtfeld (Dropdown) */}
                  {isFieldVisible('betriebsstaette') && isFieldRequired('betriebsstaette') && (
                    <div className="space-y-2">
                      <label htmlFor="clock-sf-betriebsstaette" className="block text-xs font-semibold uppercase tracking-wide text-primary pl-3">
                        {standardFieldDefs.find(f => f.name === 'betriebsstaette')?.label ?? 'Betriebsstätte'}
                        <span className="text-destructive"> *</span>
                      </label>
                      <SearchSelect
                        id="clock-sf-betriebsstaette"
                        value={(clockStandardFields['betriebsstaette'] ?? '') as string}
                        onChange={(v) => setClockStandardFields((prev) => ({ ...prev, betriebsstaette: v }))}
                        options={betriebsstaetteItems.map((item) => ({ value: item.name, label: item.name }))}
                        placeholder="– Betriebsstätte wählen –"
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                  )}

                  {/* Pflichtfelder: Typ-A Zusatzfelder */}
                  {renderCustomFieldsSection(
                    timeEntrySchema.filter((f) => f.required),
                    clockCustomFields,
                    (name, val) => setClockCustomFields((prev) => ({ ...prev, [name]: val })),
                    'clock',
                  )}

                  {/* ── Buchungsansatz V1 / V2 ────────────────────────────── */}
                  {(isFieldVisible('projekt') || isFieldVisible('projektposition') || isFieldVisible('arbeitsschritt')) && (
                    <div className="flex gap-1 rounded-md border border-input bg-muted/30 p-1 w-fit mx-auto">
                      <button
                        type="button"
                        onClick={() => setBookingMode('v1')}
                        className={`flex-1 rounded px-8 py-1.5 text-xs font-medium transition-colors ${bookingMode === 'v1' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                      >
                        Auswahl
                      </button>
                      <button
                        type="button"
                        onClick={() => setBookingMode('v2')}
                        className={`flex-1 rounded px-8 py-1.5 text-xs font-medium transition-colors ${bookingMode === 'v2' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                      >
                        Arbeitsschritt
                      </button>
                    </div>
                  )}

                  {/* ── V2: IDArbeitsschritt-Eingabe ────────────────────── */}
                  {bookingMode === 'v2' && isFieldVisible('arbeitsschritt') && (
                    <div className="space-y-2">
                      <label className="block text-xs font-semibold uppercase tracking-wide text-primary pl-3">
                        IDArbeitsschritt
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={clockV2Id}
                          onChange={(e) => setClockV2Id(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleV2Lookup(clockV2Id, 'clock')}
                          placeholder="z.B. 12345"
                          className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-foreground font-mono focus:outline-none focus:ring-2 focus:ring-ring"
                        />
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => handleV2Lookup(clockV2Id, 'clock')}
                          disabled={clockV2Loading || !clockV2Id.trim()}
                        >
                          {clockV2Loading ? '…' : 'Suchen'}
                        </Button>
                      </div>
                      {clockV2Error && <p className="text-xs text-destructive">{clockV2Error}</p>}
                      {clockV2Result && (
                        <div className="rounded-md bg-muted/50 px-3 py-2 text-xs space-y-0.5">
                          {clockV2Result.parent?.project && (
                            <p><span className="text-muted-foreground">{terms.project}:</span> {clockV2Result.parent.project.projectNumber} · {clockV2Result.parent.project.name}</p>
                          )}
                          {clockV2Result.parent && (
                            <p><span className="text-muted-foreground">{terms.position}:</span> {clockV2Result.parent.nummer != null ? `${clockV2Result.parent.nummer} – ` : ''}{clockV2Result.parent.name}</p>
                          )}
                          <p><span className="text-muted-foreground">Arbeitsschritt:</span> {clockV2Result.name}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── V1: Kaskadierte Auswahl ─────────────────────────── */}
                  {bookingMode === 'v1' && (
                    <>
                      {/* Project selection – kombiniertes Combobox-Feld */}
                      {isFieldVisible('projekt') && (
                        <div className="space-y-2">
                          <label className="block text-xs font-semibold uppercase tracking-wide text-primary pl-3">
                            {terms.project}
                            {projektRequired
                              ? <span className="text-destructive"> *</span>
                              : ` (${t('common.optional', { defaultValue: 'Optional' })})`}
                          </label>
                          <SearchSelect
                            value={clockSelectedProject}
                            onChange={(v) => setClockSelectedProject(v)}
                            options={clockProjectsUI.map((p) => ({ value: p.id, label: `${p.projectNumber} · ${p.name}` }))}
                            placeholder={`${terms.number} oder Bezeichnung…`}
                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                          />
                        </div>
                      )}

                      {/* Projektposition – nur sichtbar wenn Projekt gewählt */}
                      {isFieldVisible('projektposition') && clockSelectedProject && (
                        <div className="space-y-2">
                          <label htmlFor="clockProjektposition" className="block text-xs font-semibold uppercase tracking-wide text-primary pl-3">
                            {terms.position}
                            {isFieldRequired('projektposition')
                              ? <span className="text-destructive"> *</span>
                              : ` (${t('common.optional', { defaultValue: 'Optional' })})`}
                          </label>
                          <SearchSelect
                            id="clockProjektposition"
                            value={clockSelectedProjektposition}
                            onChange={(v) => setClockSelectedProjektposition(v)}
                            options={clockPositionsUI.map((pp) => ({ value: pp.id, label: `${pp.nummer != null ? `${pp.nummer} – ` : ''}${pp.name}` }))}
                            placeholder="Projektposition wählen"
                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                          />
                        </div>
                      )}

                      {/* Arbeitsschritt – nur sichtbar wenn Projektposition gewählt */}
                      {isFieldVisible('arbeitsschritt') && clockSelectedProjektposition && (
                        <div className="space-y-2">
                          <label htmlFor="clockArbeitsschritt" className="block text-xs font-semibold uppercase tracking-wide text-primary pl-3">
                            Arbeitsschritt
                            {isFieldRequired('arbeitsschritt')
                              ? <span className="text-destructive"> *</span>
                              : ` (${t('common.optional', { defaultValue: 'Optional' })})`}
                          </label>
                          <SearchSelect
                            id="clockArbeitsschritt"
                            value={clockSelectedArbeitsschritt}
                            onChange={(v) => setClockSelectedArbeitsschritt(v)}
                            options={clockStepsUI.map((as) => ({ value: as.id, label: as.name }))}
                            placeholder="Arbeitsschritt wählen"
                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                          />
                        </div>
                      )}
                    </>
                  )}

                  {/* Tätigkeit selection */}
                  {isFieldVisible('taetigkeit') && (
                    <div className="space-y-2">
                      <label htmlFor="clockActivity" className="block text-xs font-semibold uppercase tracking-wide text-primary pl-3">
                        {t('timeTracking.activity', { defaultValue: 'Tätigkeit' })}
                        {taetigkeitRequired
                          ? <span className="text-destructive"> *</span>
                          : ` (${t('common.optional', { defaultValue: 'Optional' })})`}
                      </label>
                      <SearchSelect
                        id="clockActivity"
                        value={clockSelectedActivity}
                        onChange={(v) => setClockSelectedActivity(v)}
                        options={clockActivitiesUI.map((activity) => ({ value: activity.id, label: activity.name }))}
                        placeholder={t('timeTracking.selectActivity', { defaultValue: 'Tätigkeit wählen' })}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                  )}

                  {/* Optionale freie Standard-Felder (ohne Hierarchie-Felder und Betriebsstätte) */}
                  {standardFieldDefs
                    .filter((f) => !f.isId && !f.isRelation && f.visible && !f.required
                      && !['projektposition', 'arbeitsschritt', 'nummerprojektposition', 'betriebsstaette'].includes(f.name))
                    .map((field) => (
                    <div key={field.name} className="space-y-2">
                      <label htmlFor={`clock-sf-${field.name}`} className="block text-xs font-semibold uppercase tracking-wide text-primary pl-3">
                        {field.label}                      </label>
                      <input
                        id={`clock-sf-${field.name}`}
                        type={field.type === 'number' ? 'number' : 'text'}
                        value={(clockStandardFields[field.name] ?? '') as string}
                        onChange={(e) => setClockStandardFields((prev) => ({ ...prev, [field.name]: e.target.value }))}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                  ))}
                  {/* Betriebsstätte – optionales Dropdown */}
                  {isFieldVisible('betriebsstaette') && !isFieldRequired('betriebsstaette') && (
                    <div className="space-y-2">
                      <label htmlFor="clock-sf-betriebsstaette-opt" className="block text-xs font-semibold uppercase tracking-wide text-primary pl-3">
                        {standardFieldDefs.find(f => f.name === 'betriebsstaette')?.label ?? 'Betriebsstätte'}{' '}
                                             </label>
                      <SearchSelect
                        id="clock-sf-betriebsstaette-opt"
                        value={(clockStandardFields['betriebsstaette'] ?? '') as string}
                        onChange={(v) => setClockStandardFields((prev) => ({ ...prev, betriebsstaette: v }))}
                        options={betriebsstaetteItems.map((item) => ({ value: item.name, label: item.name }))}
                        placeholder="– Betriebsstätte wählen –"
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                  )}

                  {/* Optionale Typ-A Zusatzfelder */}
                  {renderCustomFieldsSection(
                    timeEntrySchema.filter((f) => !f.required),
                    clockCustomFields,
                    (name, val) => setClockCustomFields((prev) => ({ ...prev, [name]: val })),
                    'clock',
                  )}

                  {/* Notiz – nur wenn Notizen in den Einstellungen erlaubt */}
                  {configAllowNotes && (
                    <div className="space-y-2">
                      <label htmlFor="clockNote" className="block text-xs font-semibold uppercase tracking-wide text-primary pl-3">
                        {t('timeTracking.note', { defaultValue: 'Notiz' })}                      </label>
                      <textarea
                        id="clockNote"
                        value={clockNote}
                        onChange={(e) => setClockNote(e.target.value)}
                        placeholder={t('timeTracking.notePlaceholder', { defaultValue: 'Optionale Notiz...' })}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                        rows={3}
                      />
                    </div>
                  )}
                </div>

                {/* Clock in button – gesperrt solange Pflichtfelder leer */}
                <Button
                  onClick={handleClockIn}
                  disabled={clockLoading || !clockInRequiredFilled}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                  size="lg"
                  title={!clockInRequiredFilled ? t('timeTracking.fillRequiredFields', { defaultValue: 'Bitte alle Pflichtfelder ausfüllen' }) : undefined}
                >
                  {clockLoading
                    ? t('common.loading')
                    : t('timeTracking.clockIn', { defaultValue: 'Einstempeln' })}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Manual Mode */}
      {!isReadonly && mode === 'manual' && (
        <div className="space-y-6">
          <div className="rounded-lg border border-primary/30 bg-muted/20 p-6">
            <div className="space-y-5">
              {/* Datum */}
              <div className="space-y-1.5">
                <label htmlFor="manualDate" className="block text-xs font-semibold uppercase tracking-wide text-primary pl-3">
                  {t('timeTracking.date', { defaultValue: 'Datum' })}
                </label>
                <input
                  id="manualDate"
                  type="date"
                  value={manualDate}
                  onChange={(e) => setManualDate(e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              {/* Von / Bis – 2-Spalten-Grid */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label htmlFor="manualStartTime" className="block text-xs font-semibold uppercase tracking-wide text-primary pl-3">
                    {t('timeTracking.startTime', { defaultValue: 'Von' })}
                  </label>
                  <input
                    id="manualStartTime"
                    type="time"
                    value={manualStartTime}
                    onChange={(e) => setManualStartTime(e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="manualEndTime" className="block text-xs font-semibold uppercase tracking-wide text-primary pl-3">
                    {t('timeTracking.endTime', { defaultValue: 'Bis' })}
                  </label>
                  <input
                    id="manualEndTime"
                    type="time"
                    value={manualEndTime}
                    onChange={(e) => setManualEndTime(e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              </div>

              {/* Pflichtfelder: freie Standard-Felder (ohne Hierarchie-Felder und Betriebsstätte) */}
              {standardFieldDefs
                .filter((f) => !f.isId && !f.isRelation && f.visible && f.required
                  && !['projektposition', 'arbeitsschritt', 'nummerprojektposition', 'betriebsstaette'].includes(f.name))
                .map((field) => (
                  <div key={field.name} className="space-y-2">
                    <label htmlFor={`manual-sf-${field.name}`} className="block text-xs font-semibold uppercase tracking-wide text-primary pl-3">
                      {field.label}<span className="text-destructive"> *</span>
                    </label>
                    <input
                      id={`manual-sf-${field.name}`}
                      type={field.type === 'number' ? 'number' : 'text'}
                      value={(manualStandardFields[field.name] ?? '') as string}
                      onChange={(e) => setManualStandardFields((prev) => ({ ...prev, [field.name]: e.target.value }))}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
              ))}
              {/* Betriebsstätte – Pflichtfeld (Dropdown) */}
              {isFieldVisible('betriebsstaette') && isFieldRequired('betriebsstaette') && (
                <div className="space-y-2">
                  <label htmlFor="manual-sf-betriebsstaette" className="block text-xs font-semibold uppercase tracking-wide text-primary pl-3">
                    {standardFieldDefs.find(f => f.name === 'betriebsstaette')?.label ?? 'Betriebsstätte'}
                    <span className="text-destructive"> *</span>
                  </label>
                  <SearchSelect
                    id="manual-sf-betriebsstaette"
                    value={(manualStandardFields['betriebsstaette'] ?? '') as string}
                    onChange={(v) => setManualStandardFields((prev) => ({ ...prev, betriebsstaette: v }))}
                    options={betriebsstaetteItems.map((item) => ({ value: item.name, label: item.name }))}
                    placeholder="– Betriebsstätte wählen –"
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              )}

              {/* Pflichtfelder: Typ-A Zusatzfelder */}
              {renderCustomFieldsSection(
                timeEntrySchema.filter((f) => f.required),
                manualCustomFields,
                (name, val) => setManualCustomFields((prev) => ({ ...prev, [name]: val })),
                'manual',
              )}

              {/* ── Buchungsansatz V1 / V2 (Manual Mode) ─────────────── */}
              {(isFieldVisible('projekt') || isFieldVisible('projektposition') || isFieldVisible('arbeitsschritt')) && (
                <div className="flex gap-1 rounded-md border border-input bg-muted/30 p-1">
                  <button
                    type="button"
                    onClick={() => setBookingMode('v1')}
                    className={`flex-1 rounded px-3 py-1.5 text-xs font-medium transition-colors ${bookingMode === 'v1' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    Auswahl
                  </button>
                  <button
                    type="button"
                    onClick={() => setBookingMode('v2')}
                    className={`flex-1 rounded px-3 py-1.5 text-xs font-medium transition-colors ${bookingMode === 'v2' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    Arbeitsschritt
                  </button>
                </div>
              )}

              {/* V2: IDArbeitsschritt-Eingabe (Manual) */}
              {bookingMode === 'v2' && isFieldVisible('arbeitsschritt') && (
                <div className="space-y-2">
                  <label className="block text-xs font-semibold uppercase tracking-wide text-primary pl-3">IDArbeitsschritt</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={manualV2Id}
                      onChange={(e) => setManualV2Id(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleV2Lookup(manualV2Id, 'manual')}
                      placeholder="z.B. 12345"
                      className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-foreground font-mono focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                    <Button type="button" size="sm" onClick={() => handleV2Lookup(manualV2Id, 'manual')} disabled={manualV2Loading || !manualV2Id.trim()}>
                      {manualV2Loading ? '…' : 'Suchen'}
                    </Button>
                  </div>
                  {manualV2Error && <p className="text-xs text-destructive">{manualV2Error}</p>}
                  {manualV2Result && (
                    <div className="rounded-md bg-muted/50 px-3 py-2 text-xs space-y-0.5">
                      {manualV2Result.parent?.project && (
                        <p><span className="text-muted-foreground">{terms.project}:</span> {manualV2Result.parent.project.projectNumber} · {manualV2Result.parent.project.name}</p>
                      )}
                      {manualV2Result.parent && (
                        <p><span className="text-muted-foreground">{terms.position}:</span> {manualV2Result.parent.nummer != null ? `${manualV2Result.parent.nummer} – ` : ''}{manualV2Result.parent.name}</p>
                      )}
                      <p><span className="text-muted-foreground">Arbeitsschritt:</span> {manualV2Result.name}</p>
                    </div>
                  )}
                </div>
              )}

              {/* V1: Kaskadierte Auswahl (Manual) */}
              {bookingMode === 'v1' && (
                <>
                  {isFieldVisible('projekt') && (
                    <div className="space-y-2">
                      <label className="block text-xs font-semibold uppercase tracking-wide text-primary pl-3">
                        {terms.project}
                        {projektRequired ? <span className="text-destructive"> *</span> : ` (${t('common.optional', { defaultValue: 'Optional' })})`}
                      </label>
                      <SearchSelect
                        value={manualSelectedProject}
                        onChange={(v) => setManualSelectedProject(v)}
                        options={manualProjectsUI.map((p) => ({ value: p.id, label: `${p.projectNumber} · ${p.name}` }))}
                        placeholder={`${terms.number} oder Bezeichnung…`}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                  )}
                  {isFieldVisible('projektposition') && manualSelectedProject && (
                    <div className="space-y-2">
                      <label htmlFor="manualProjektposition" className="block text-xs font-semibold uppercase tracking-wide text-primary pl-3">
                        Projektposition
                        {isFieldRequired('projektposition') ? <span className="text-destructive"> *</span> : ` (${t('common.optional', { defaultValue: 'Optional' })})`}
                      </label>
                      <SearchSelect
                        id="manualProjektposition"
                        value={manualSelectedProjektposition}
                        onChange={(v) => setManualSelectedProjektposition(v)}
                        options={manualPositionsUI.map((pp) => ({ value: pp.id, label: `${pp.nummer != null ? `${pp.nummer} – ` : ''}${pp.name}` }))}
                        placeholder="Projektposition wählen"
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                  )}
                  {isFieldVisible('arbeitsschritt') && manualSelectedProjektposition && (
                    <div className="space-y-2">
                      <label htmlFor="manualArbeitsschritt" className="block text-xs font-semibold uppercase tracking-wide text-primary pl-3">
                        Arbeitsschritt
                        {isFieldRequired('arbeitsschritt') ? <span className="text-destructive"> *</span> : ` (${t('common.optional', { defaultValue: 'Optional' })})`}
                      </label>
                      <SearchSelect
                        id="manualArbeitsschritt"
                        value={manualSelectedArbeitsschritt}
                        onChange={(v) => setManualSelectedArbeitsschritt(v)}
                        options={manualStepsUI.map((as) => ({ value: as.id, label: as.name }))}
                        placeholder="Arbeitsschritt wählen"
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                  )}
                </>
              )}

              {/* Tätigkeit */}
              {isFieldVisible('taetigkeit') && (
                <div className="space-y-2">
                  <label htmlFor="manualActivity" className="block text-xs font-semibold uppercase tracking-wide text-primary pl-3">
                    {t('timeTracking.activity', { defaultValue: 'Tätigkeit' })}
                    {taetigkeitRequired ? <span className="text-destructive"> *</span> : ` (${t('common.optional', { defaultValue: 'Optional' })})`}
                  </label>
                  <SearchSelect
                    id="manualActivity"
                    value={manualSelectedActivity}
                    onChange={(v) => setManualSelectedActivity(v)}
                    options={manualActivitiesUI.map((activity) => ({ value: activity.id, label: activity.name }))}
                    placeholder={t('timeTracking.selectActivity', { defaultValue: 'Tätigkeit wählen' })}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              )}

              {/* Break – nur wenn der Admin Pausenminuten aktiviert hat */}
              {breakMinutesEnabled && (
              <div className="space-y-2">
                <label htmlFor="manualBreak" className="block text-xs font-semibold uppercase tracking-wide text-primary pl-3">
                  {t('timeTracking.breakMinutes', { defaultValue: 'Pausenminuten' })}                </label>
                <input
                  id="manualBreak"
                  type="number"
                  min="0"
                  value={manualBreak}
                  onChange={(e) => setManualBreak(Math.max(0, parseInt(e.target.value) || 0))}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
              )}

              {/* Optionale freie Standard-Felder (ohne Hierarchie-Felder und Betriebsstätte) */}
              {standardFieldDefs
                .filter((f) => !f.isId && !f.isRelation && f.visible && !f.required
                  && !['projektposition', 'arbeitsschritt', 'nummerprojektposition', 'betriebsstaette'].includes(f.name))
                .map((field) => (
                  <div key={field.name} className="space-y-2">
                    <label htmlFor={`manual-sf-${field.name}`} className="block text-xs font-semibold uppercase tracking-wide text-primary pl-3">
                      {field.label}                    </label>
                    <input
                      id={`manual-sf-${field.name}`}
                      type={field.type === 'number' ? 'number' : 'text'}
                      value={(manualStandardFields[field.name] ?? '') as string}
                      onChange={(e) => setManualStandardFields((prev) => ({ ...prev, [field.name]: e.target.value }))}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
              ))}
              {/* Betriebsstätte – optionales Dropdown */}
              {isFieldVisible('betriebsstaette') && !isFieldRequired('betriebsstaette') && (
                <div className="space-y-2">
                  <label htmlFor="manual-sf-betriebsstaette-opt" className="block text-xs font-semibold uppercase tracking-wide text-primary pl-3">
                    {standardFieldDefs.find(f => f.name === 'betriebsstaette')?.label ?? 'Betriebsstätte'}{' '}
                                     </label>
                  <SearchSelect
                    id="manual-sf-betriebsstaette-opt"
                    value={(manualStandardFields['betriebsstaette'] ?? '') as string}
                    onChange={(v) => setManualStandardFields((prev) => ({ ...prev, betriebsstaette: v }))}
                    options={betriebsstaetteItems.map((item) => ({ value: item.name, label: item.name }))}
                    placeholder="– Betriebsstätte wählen –"
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              )}

              {/* Optionale Typ-A Zusatzfelder */}
              {renderCustomFieldsSection(
                timeEntrySchema.filter((f) => !f.required),
                manualCustomFields,
                (name, val) => setManualCustomFields((prev) => ({ ...prev, [name]: val })),
                'manual',
              )}

              {/* Notiz – nur wenn Notizen in den Einstellungen erlaubt */}
              {configAllowNotes && (
                <div className="space-y-2">
                  <label htmlFor="manualNote" className="block text-xs font-semibold uppercase tracking-wide text-primary pl-3">
                    {t('timeTracking.note', { defaultValue: 'Notiz' })}                  </label>
                  <textarea
                    id="manualNote"
                    value={manualNote}
                    onChange={(e) => setManualNote(e.target.value)}
                    placeholder={t('timeTracking.notePlaceholder', { defaultValue: 'Optionale Notiz...' })}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    rows={3}
                  />
                </div>
              )}

              {/* Fehlermeldung Manuelle Erfassung */}
              {manualError && (
                <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 dark:border-red-700 dark:bg-red-950">
                  <p className="text-sm font-medium text-red-700 dark:text-red-300">{manualError}</p>
                </div>
              )}

              {/* Submit button – gesperrt solange Pflichtfelder leer */}
              <Button
                onClick={handleCreateManualEntry}
                disabled={manualLoading || !manualRequiredFilled}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                size="lg"
                title={!manualRequiredFilled ? t('timeTracking.fillRequiredFields', { defaultValue: 'Bitte alle Pflichtfelder ausfüllen' }) : undefined}
              >
                {manualLoading
                  ? t('common.loading')
                  : t('timeTracking.submit', { defaultValue: 'Eintrag erstellen' })}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Diäten-Reiter (Tagesdaten) — Inline-Formular für gewählten MA + Datum */}
      {!isReadonly && mode === 'diaeten' && (
        <div className="space-y-4">
          <div className="rounded-lg border border-primary/30 bg-muted/20 p-4">
            <label htmlFor="diaetenDate" className="block text-xs font-semibold uppercase tracking-wide text-primary pl-3">
              {t('timeTracking.date', { defaultValue: 'Datum' })}
            </label>
            <input
              id="diaetenDate"
              type="date"
              value={diaetenDate}
              onChange={(e) => setDiaetenDate(e.target.value)}
              className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <DailyRecordDialog
            inline
            open
            date={diaetenDate}
            userId={targetEmployeeId}
            manualHours={manualHours}
            dayFields={dayFields}
            onClose={() => { /* inline: kein Schließen */ }}
            onSaved={handleDailyRecordSaved}
          />
        </div>
      )}

      {/* Fehlzeit-Reiter — Erfassungsformular (wie in der PWA) */}
      {!isReadonly && mode === 'fehlzeit' && (
        <div className="space-y-6">
          <div className="rounded-lg border border-primary/30 bg-muted/20 p-6 space-y-5">
            {absenceTypes.length === 0 ? (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300">
                {t('timeTracking.noAbsenceTypes', { defaultValue: 'Es sind noch keine Fehlzeit-Typen angelegt. Bitte wende dich an deinen Administrator.' })}
              </div>
            ) : (
              <>
                <div className="space-y-1.5">
                  <label htmlFor="fzType" className="block text-xs font-semibold uppercase tracking-wide text-primary pl-3">
                    {t('timeTracking.absenceType', { defaultValue: 'Fehlzeit-Typ' })}
                  </label>
                  <select
                    id="fzType"
                    value={fzTypeId}
                    onChange={(e) => setFzTypeId(e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    {absenceTypes.map((tp) => <option key={tp.id} value={tp.id}>{tp.name}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="fzDate" className="block text-xs font-semibold uppercase tracking-wide text-primary pl-3">
                    {t('timeTracking.date', { defaultValue: 'Datum' })}
                  </label>
                  <input
                    id="fzDate"
                    type="date"
                    value={fzDate}
                    onChange={(e) => setFzDate(e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="fzHours" className="block text-xs font-semibold uppercase tracking-wide text-primary pl-3">
                    {t('timeTracking.hours', { defaultValue: 'Stunden' })}
                  </label>
                  <input
                    id="fzHours"
                    type="number"
                    min={0.25}
                    step={0.25}
                    value={fzHours}
                    onChange={(e) => { setFzHours(e.target.value); setFzHoursTouched(true) }}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('timeTracking.hoursHint', { defaultValue: 'z.B. 8 für einen ganzen Tag, 2 für einen Arztbesuch.' })}
                  </p>
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="fzNote" className="block text-xs font-semibold uppercase tracking-wide text-primary pl-3">
                    {t('timeTracking.noteOptional', { defaultValue: 'Notiz (optional)' })}
                  </label>
                  <textarea
                    id="fzNote"
                    rows={2}
                    value={fzNote}
                    onChange={(e) => setFzNote(e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>

                {fzError && (
                  <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 dark:border-red-700 dark:bg-red-950">
                    <p className="text-sm font-medium text-red-700 dark:text-red-300">{fzError}</p>
                  </div>
                )}

                <Button
                  onClick={handleFehlzeitSave}
                  disabled={fzSaving}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                  size="lg"
                >
                  {fzSuccess
                    ? t('timeTracking.saved', { defaultValue: 'Gespeichert' })
                    : fzSaving
                      ? t('common.saving', { defaultValue: 'Speichert...' })
                      : t('timeTracking.saveAbsence', { defaultValue: 'Fehlzeit speichern' })}
                </Button>
              </>
            )}
          </div>

          {/* Zuletzt erfasste Fehlzeiten des gewählten Mitarbeiters */}
          <div>
            <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
              {t('timeTracking.recentAbsences', { defaultValue: 'Zuletzt erfasst' })}
            </h3>
            <div className="space-y-2">
              {recentAbsences.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  {t('timeTracking.noAbsencesYet', { defaultValue: 'Noch keine Fehlzeiten erfasst.' })}
                </p>
              )}
              {recentAbsences.map((a) => (
                <div key={a.id} className="flex items-center justify-between rounded-lg border border-border bg-background px-3 py-2.5">
                  <div className="flex items-center gap-3">
                    <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: a.absenceType?.color ?? 'hsl(var(--muted-foreground))' }} />
                    <div>
                      <div className="text-sm font-medium text-foreground">{a.absenceType?.name ?? '—'} · {formatHoursDecimal(a.durationMinutes)}</div>
                      <div className="text-xs text-muted-foreground">{fmtDateLong(a.date)}{a.note ? ` · ${a.note}` : ''}</div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDeleteAbsence(a)}
                    title={t('common.delete', { defaultValue: 'Löschen' })}
                    className="rounded p-1.5 text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tagesansicht: navigierbar */}
      <h2 className="text-2xl font-semibold text-primary">
        {t('timeTracking.daySummary', { defaultValue: 'Tageszusammenfassung' })}
      </h2>
      <div className="rounded-lg border border-border bg-background shadow-sm">
        {/* Navigation Header */}
        <div className="flex items-center gap-2 border-b border-border px-4 py-3">
          <button
            type="button"
            onClick={navigatePrevDay}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            title="Vorheriger Tag"
          >
            <span className="text-4xl leading-none">‹</span>
          </button>
          <div className="flex flex-1 items-center gap-2">
            <h2 className="flex-1 text-center text-base font-semibold text-primary">
              {formatViewDate(viewDate)}
            </h2>
            <input
              type="date"
              value={viewDate}
              max={todayStr}
              onChange={(e) => e.target.value && setViewDate(e.target.value)}
              className="rounded-md border border-input bg-background px-2 py-1 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              title="Zu einem bestimmten Tag springen"
            />
          </div>
          <button
            type="button"
            onClick={navigateNextDay}
            disabled={viewDate >= todayStr}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title="Nächster Tag"
          >
            <span className="text-4xl leading-none">›</span>
          </button>
        </div>

        {/* Einträge */}
        <div className="space-y-4 p-4">
        {loading ? (
          <p className="text-muted-foreground">{t('common.loading')}</p>
        ) : entries.length === 0 && dayAbsences.length === 0 ? (
          <p className="text-muted-foreground">
            {t('timeTracking.noEntriesForDay', { defaultValue: 'Keine Einträge für diesen Tag' })}
          </p>
        ) : entries.length === 0 ? null : (
          <div className="space-y-3">
            {entries.map((entry) => (
              <React.Fragment key={entry.id}>
              <div
                className={`rounded-lg border border-primary/30 p-4 transition-colors ${
                  deletingEntryId === entry.id
                    ? 'border-red-400 bg-red-50 dark:border-red-600 dark:bg-red-950/40'
                    : 'bg-background'
                }`}
              >
                {/* Inhalt + Buttons: nebeneinander auf breiten Screens, untereinander auf schmalen */}
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium text-foreground">
                        {formatEntryTime(entry.startTime)}
                        {entry.endTime ? ` – ${formatEntryTime(entry.endTime)}` : ' – …'}
                      </p>
                      <span
                        className={`inline-block rounded-full px-2 py-1 text-xs font-medium ${
                          entry.status === 'DRAFT'
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                            : entry.status === 'SAVED'
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                            : entry.status === 'SYNCED'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                        }`}
                      >
                        {translateStatus(entry.status)}
                      </span>
                    </div>

                    {(() => {
                      // AP-13 Bug-Fix: Ref-Pfad hat Vorrang vor Legacy.
                      const projName = entry.projectRef?.name ?? entry.project?.name
                      const projNumber = entry.projectRef?.projectNumber ?? entry.project?.projectNumber
                      return projName ? (
                        <p className="text-sm text-muted-foreground">
                          {terms.project}: {projectLabel(projNumber, projName)}
                        </p>
                      ) : null
                    })()}

                    {(() => {
                      const act = entry.activityRef ?? entry.activityType
                      if (!act) return null
                      const color = (act as { color?: string }).color ?? '#6b7280'
                      return (
                        <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                          <span
                            className="inline-block h-2 w-2 rounded-full shrink-0"
                            style={{ backgroundColor: color }}
                          />
                          {t('timeTracking.activity', { defaultValue: 'Aktivität' })}: {act.name}
                        </p>
                      )
                    })()}

                    {entry.duration && (
                      <p className="text-sm text-muted-foreground">
                        {t('timeTracking.duration', { defaultValue: 'Dauer' })}: {formatDuration(entry.duration)}
                        {entry.breakMinutes ? ` (${entry.breakMinutes}m ${t('timeTracking.break', { defaultValue: 'Pause' })})` : ''}
                      </p>
                    )}

                    {entry.note && (
                      <p className="text-sm text-muted-foreground italic">"{entry.note}"</p>
                    )}
                  </div>

                  {/* Bearbeiten / Löschen – nur wenn Policy es erlaubt (nie für READONLY) */}
                  {!isReadonly && (canEditEntry(entry) || canDeleteEntry(entry)) && editingEntryId !== entry.id && deletingEntryId !== entry.id && (
                    <div className="flex shrink-0 gap-1">
                      {canEditEntry(entry) && (
                        <button
                          onClick={() => handleStartEdit(entry)}
                          title="Bearbeiten"
                          className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      )}
                      {canDeleteEntry(entry) && (
                        <button
                          onClick={() => { setDeletingEntryId(entry.id); setEditingEntryId(null) }}
                          title="Löschen"
                          className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* ── Inline Edit-Formular ──────────────────────────────── */}
              {editingEntryId === entry.id && (
                <div className="mt-3 space-y-3 rounded-md border border-border bg-muted/20 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Eintrag bearbeiten</p>

                  {editError && (
                    <p className="text-xs text-destructive">{editError}</p>
                  )}

                  {/* Zeit */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="block text-xs text-muted-foreground">Von</label>
                      <input
                        type="time"
                        value={editForm.startTime}
                        onChange={(e) => setEditForm((p) => ({ ...p, startTime: e.target.value }))}
                        className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="block text-xs text-muted-foreground">Bis</label>
                      <input
                        type="time"
                        value={editForm.endTime}
                        onChange={(e) => setEditForm((p) => ({ ...p, endTime: e.target.value }))}
                        className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                  </div>

                  {/* Betriebsstätte – Dropdown wie Buchungsmaske */}
                  {isFieldVisible('betriebsstaette') && (
                    <div className="space-y-1">
                      <label className="block text-xs text-muted-foreground">
                        {standardFieldDefs.find(f => f.name === 'betriebsstaette')?.label ?? 'Betriebsstätte'}
                        {isFieldRequired('betriebsstaette') && <span className="text-destructive"> *</span>}
                      </label>
                      <SearchSelect
                        value={editForm.standardFields['betriebsstaette'] ?? ''}
                        onChange={(v) => setEditForm((p) => ({ ...p, standardFields: { ...p.standardFields, betriebsstaette: v } }))}
                        options={betriebsstaetteItems.map((item) => ({ value: item.name, label: item.name }))}
                        placeholder="– Betriebsstätte wählen –"
                        className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                  )}

                  {/* Projekt (ref-fähig) */}
                  {isFieldVisible('projekt') && clockProjectsUI.length > 0 && (
                    <div className="space-y-1">
                      <label className="block text-xs text-muted-foreground">{terms.project}</label>
                      <SearchSelect
                        value={clockProjectIsRef ? editForm.projectRefId : editForm.projectId}
                        onChange={(v) => setEditForm((p) => ({
                          ...p,
                          [clockProjectIsRef ? 'projectRefId' : 'projectId']: v,
                          // Kaskade zurücksetzen wenn Projekt wechselt
                          selectedProjektposition: '',
                          selectedArbeitsschritt: '',
                        }))}
                        options={clockProjectsUI.map((p) => ({ value: p.id, label: `${p.projectNumber ? `${p.projectNumber} · ` : ''}${p.name}` }))}
                        placeholder={`– ${terms.none.replace(/^Kein(e)? /, 'Kein ')} –`}
                        className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                  )}

                  {/* Projektposition – kaskadierendes Dropdown wie Buchungsmaske */}
                  {isFieldVisible('projektposition') && (clockProjectIsRef ? editForm.projectRefId : editForm.projectId) && (
                    <div className="space-y-1">
                      <label className="block text-xs text-muted-foreground">
                        {terms.position}{isFieldRequired('projektposition') && <span className="text-destructive"> *</span>}
                      </label>
                      <SearchSelect
                        value={editForm.selectedProjektposition}
                        onChange={(v) => setEditForm((p) => ({ ...p, selectedProjektposition: v, selectedArbeitsschritt: '' }))}
                        options={editPositionsUI.map((pp) => ({ value: pp.id, label: `${pp.nummer != null ? `${pp.nummer} – ` : ''}${pp.name}` }))}
                        placeholder="Projektposition wählen"
                        className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                  )}

                  {/* Arbeitsschritt – kaskadierendes Dropdown wie Buchungsmaske */}
                  {isFieldVisible('arbeitsschritt') && editForm.selectedProjektposition && (
                    <div className="space-y-1">
                      <label className="block text-xs text-muted-foreground">
                        Arbeitsschritt{isFieldRequired('arbeitsschritt') && <span className="text-destructive"> *</span>}
                      </label>
                      <SearchSelect
                        value={editForm.selectedArbeitsschritt}
                        onChange={(v) => setEditForm((p) => ({ ...p, selectedArbeitsschritt: v }))}
                        options={editStepsUI.map((as) => ({ value: as.id, label: as.name }))}
                        placeholder="Arbeitsschritt wählen"
                        className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                  )}

                  {/* Tätigkeit (ref-fähig) */}
                  {clockActivitiesUI.length > 0 && (
                    <div className="space-y-1">
                      <label className="block text-xs text-muted-foreground">Tätigkeit</label>
                      <SearchSelect
                        value={clockActivityIsRef ? editForm.activityRefId : editForm.activityTypeId}
                        onChange={(v) => setEditForm((p) => ({ ...p, [clockActivityIsRef ? 'activityRefId' : 'activityTypeId']: v }))}
                        options={clockActivitiesUI.map((a) => ({ value: a.id, label: a.name }))}
                        placeholder="– Keine Tätigkeit –"
                        className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                  )}

                  {/* Pause */}
                  {breakMinutesEnabled && (
                    <div className="space-y-1">
                      <label className="block text-xs text-muted-foreground">Pause (Minuten)</label>
                      <input
                        type="number"
                        min={0}
                        value={editForm.breakMinutes}
                        onChange={(e) => setEditForm((p) => ({ ...p, breakMinutes: Number(e.target.value) }))}
                        className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                  )}

                  {/* Zusatzfelder Typ A */}
                  {timeEntrySchema.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs text-muted-foreground">Zusatzfelder</p>
                      {timeEntrySchema.map((field) => {
                        const val = editForm.customFields[field.name] ?? (field.type === 'checkbox' ? false : '')
                        if (field.type === 'checkbox') return (
                          <div key={field.name} className="flex items-center gap-2">
                            <input type="checkbox" id={`edit-cf-${field.name}`}
                              checked={!!val}
                              onChange={(e) => setEditForm((p) => ({ ...p, customFields: { ...p.customFields, [field.name]: e.target.checked } }))}
                              className="h-4 w-4 rounded border-input"
                            />
                            <label htmlFor={`edit-cf-${field.name}`} className="text-xs text-foreground">{field.label}</label>
                          </div>
                        )
                        if (field.options && field.options.length > 0) return (
                          <div key={field.name} className="space-y-1">
                            <label className="block text-xs text-muted-foreground">{field.label}</label>
                            <SearchSelect
                              value={val as string}
                              onChange={(v) => setEditForm((p) => ({ ...p, customFields: { ...p.customFields, [field.name]: v } }))}
                              options={(field.options ?? []).map((opt) => ({ value: opt, label: opt }))}
                              placeholder="Wählen…"
                              className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                            />
                          </div>
                        )
                        return (
                          <div key={field.name} className="space-y-1">
                            <label className="block text-xs text-muted-foreground">{field.label}</label>
                            <input
                              type={field.type === 'number' ? 'number' : field.type === 'time' ? 'time' : 'text'}
                              value={val as string}
                              onChange={(e) => setEditForm((p) => ({ ...p, customFields: { ...p.customFields, [field.name]: field.type === 'number' ? Number(e.target.value) : e.target.value } }))}
                              className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                            />
                          </div>
                        )
                      })}
                    </div>
                  )}

                  {/* Notiz */}
                  {configAllowNotes && (
                    <div className="space-y-1">
                      <label className="block text-xs text-muted-foreground">Notiz</label>
                      <input
                        type="text"
                        value={editForm.note}
                        onChange={(e) => setEditForm((p) => ({ ...p, note: e.target.value }))}
                        placeholder="Optionale Notiz…"
                        className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                  )}

                  {/* Begründung (nur bei WITH_NOTE-Policy) */}
                  {editPolicy === 'WITH_NOTE' && (
                    <div className="space-y-1">
                      <label className="block text-xs text-muted-foreground">
                        Begründung <span className="text-destructive">*</span>
                      </label>
                      <input
                        type="text"
                        value={editForm.editNote}
                        onChange={(e) => setEditForm((p) => ({ ...p, editNote: e.target.value }))}
                        placeholder="Warum wird dieser Eintrag geändert?"
                        className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                  )}

                  <div className="flex gap-2 pt-1">
                    <Button
                      size="sm"
                      onClick={() => handleSaveEdit(entry)}
                      disabled={editLoading}
                      className="bg-primary text-primary-foreground hover:bg-primary/90"
                    >
                      {editLoading ? 'Wird gespeichert…' : 'Speichern'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCancelEdit}
                      disabled={editLoading}
                      className="border-border text-foreground hover:bg-muted"
                    >
                      Abbrechen
                    </Button>
                  </div>
                </div>
              )}

              {/* ── Löschen-Bestätigung ───────────────────────────────── */}
              {deletingEntryId === entry.id && (
                <div className="mt-3 flex items-center gap-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2">
                  <p className="flex-1 text-sm text-foreground">
                    Diesen Eintrag wirklich löschen?
                  </p>
                  <Button
                    size="sm"
                    onClick={() => handleDeleteEntry(entry.id)}
                    disabled={deleteLoading}
                    className="bg-destructive text-white hover:bg-destructive/90"
                  >
                    {deleteLoading ? '…' : 'Ja, löschen'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setDeletingEntryId(null)}
                    disabled={deleteLoading}
                    className="border-border text-foreground hover:bg-muted"
                  >
                    Abbrechen
                  </Button>
                </div>
              )}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* AP-19: Fehlzeiten des Tages – einzelne Einträge */}
        {!loading && dayAbsences.length > 0 && (
          <div className="space-y-3">
            {dayAbsences.map((a) => (
              <div key={a.id} className="rounded-lg border border-amber-400/40 bg-amber-50/60 p-4 dark:border-amber-700/40 dark:bg-amber-950/20">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-block h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: a.absenceType?.color ?? '#6b7280' }} />
                  <p className="font-medium text-foreground">{a.absenceType?.name ?? 'Fehlzeit'}</p>
                  <span className="inline-block rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-400">Fehlzeit</span>
                  <span className="ml-auto text-sm font-medium text-foreground">{formatHoursDecimal(a.durationMinutes)}</span>
                </div>
                {a.note && <p className="mt-1 text-sm text-muted-foreground italic">"{a.note}"</p>}
              </div>
            ))}
          </div>
        )}

        {/* Tageszusammenfassung – innerhalb des Rahmens */}
        {!loading && (entries.length > 0 || dailyRecord || dayAbsences.length > 0) && (
          <div className="rounded-md border border-primary/20 bg-muted/20 p-4 space-y-3 mt-2">

            {/* Gesamtarbeitszeit */}
            {entries.length > 0 && (() => {
              const totalMinutes = entries.reduce((sum, e) => sum + (e.duration ?? 0), 0)
              return (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    {t('timeTracking.totalWorkTime', { defaultValue: 'Gesamtarbeitszeit' })}
                  </span>
                  <span className="text-sm font-medium text-foreground">
                    {formatHoursDecimal(totalMinutes)}
                  </span>
                </div>
              )
            })()}

            {/* AP-19: Fehlzeiten gesamt (Tag) */}
            {dayAbsences.length > 0 && (
              <div className={`flex items-center justify-between ${entries.length > 0 ? 'border-t border-border pt-3' : ''}`}>
                <span className="text-sm text-muted-foreground">Fehlzeiten gesamt</span>
                <span className="text-sm font-medium text-foreground">
                  {formatHoursDecimal(dayAbsences.reduce((sum, a) => sum + a.durationMinutes, 0))}
                </span>
              </div>
            )}

            {/* Diäten-Felder */}
            {dailyRecord?.customFields && Object.keys(dailyRecord.customFields).length > 0 && (
              <div className="space-y-1 border-t border-border pt-3">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
                  {t('timeTracking.dailyRecord', { defaultValue: 'Tagesdaten' })}
                </p>
                {Object.entries(dailyRecord.customFields).map(([key, value]) => {
                  const fieldDef = dailyRecordSchema.find((f) => f.name === key)
                  const label = fieldDef?.label ?? key
                  const unit = fieldDef?.unit ? ` ${fieldDef.unit}` : ''
                  const displayValue =
                    typeof value === 'boolean'
                      ? value ? 'Ja' : 'Nein'
                      : String(value ?? '–')
                  return (
                    <div key={key} className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{label}</span>
                      <span className="text-sm font-medium text-foreground">
                        {displayValue}{unit}
                      </span>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}
        </div>{/* end p-4 Einträge-Bereich */}

      </div>{/* end navigierbarer Rahmen */}

      {/* ── Wochenzusammenfassung ───────────────────────────── */}
      {(() => {
        const diaeten = sumDiaeten(weekDailyRecords)
        const diaetenEntries = Object.entries(diaeten).filter(([, v]) =>
          v.mode === 'grouped' ? Object.keys(v.groups).length > 0 : v.value !== 0
        )
        const weekRanges = getPeriodRanges(weekAnchor)
        const isCurrentWeek = weekAnchor >= weekRanges.week.from && weekAnchor <= weekRanges.week.to && weekRanges.week.to >= todayStr
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-semibold text-primary">Wochenzusammenfassung</h2>
            <div className="relative rounded-lg border border-border bg-background shadow-sm">
              <LoadingOverlay show={weekLoading} />
              <div className="flex items-center gap-2 border-b border-border px-4 py-3">
                <button type="button" onClick={navigatePrevWeek} className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
                  <span className="text-4xl leading-none">‹</span>
                </button>
                <div className="flex flex-1 items-center gap-2">
                  <h3 className="flex-1 text-center text-base font-semibold text-primary">{formatWeekHeader(weekAnchor)}</h3>
                  <input
                    type="date" value={weekAnchor} max={todayStr}
                    onChange={(e) => e.target.value && setWeekAnchor(e.target.value)}
                    className="rounded-md border border-input bg-background px-2 py-1 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    title="Zu einer bestimmten Woche springen"
                  />
                </div>
                <button type="button" onClick={navigateNextWeek} disabled={isCurrentWeek}
                  className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                  <span className="text-4xl leading-none">›</span>
                </button>
              </div>
              <div className="p-4 space-y-2">
                {/* AP-19: Projektsummen (Woche) – darunter die Gesamtarbeitszeit */}
                {(() => {
                  const projectSums = sumEntriesByProject(weekEntries)
                  return projectSums.length > 0 ? (
                    <div className="space-y-1 pb-1">
                      <p className="text-xs font-semibold uppercase tracking-wide text-primary pl-1 pb-1">{terms.projectPlural}</p>
                      {projectSums.map((p) => (
                        <div key={p.name} className="flex items-center justify-between text-sm pl-1">
                          <span className="text-muted-foreground">{p.name}</span>
                          <span className="font-medium text-foreground">{formatHoursDecimal(p.minutes)}</span>
                        </div>
                      ))}
                    </div>
                  ) : null
                })()}
                <div className={`flex items-center justify-between text-sm ${weekEntries.length > 0 ? 'border-t border-border pt-2' : ''}`}>
                  <span className="text-muted-foreground">Gesamtarbeitszeit</span>
                  <span className="font-medium text-foreground">{weekSummary != null ? formatHoursDecimal(weekSummary.totalMinutes) : '–'}</span>
                </div>
                {/* AP-19: Fehlzeiten je Typ */}
                {(() => {
                  const absenceSums = sumAbsencesByType(weekAbsences)
                  return absenceSums.length > 0 ? (
                    <div className="space-y-1 border-t border-border pt-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-primary pl-1 pb-1">Fehlzeiten</p>
                      {absenceSums.map((a) => (
                        <div key={a.name} className="flex items-center justify-between text-sm pl-1">
                          <span className="text-muted-foreground flex items-center gap-1.5">
                            <span className="inline-block h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: a.color ?? '#6b7280' }} />
                            {a.name}
                          </span>
                          <span className="font-medium text-foreground">{formatHoursDecimal(a.minutes)}</span>
                        </div>
                      ))}
                    </div>
                  ) : null
                })()}
                {diaetenEntries.length > 0 && (
                  <div className="space-y-1 border-t border-border pt-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-primary pl-1 pb-1">Diäten</p>
                    {diaetenEntries.map(([fieldKey, entry]) => {
                      const fieldDef = dailyRecordSchema.find((f) => f.name === fieldKey)
                      const lbl = fieldDef?.label ?? fieldKey
                      if (entry.mode === 'grouped') return (
                        <div key={fieldKey} className="space-y-0.5">
                          <span className="text-sm text-muted-foreground">{lbl}</span>
                          {Object.entries(entry.groups).map(([optVal, count]) => (
                            <div key={optVal} className="flex items-center justify-between text-sm pl-3">
                              <span className="text-muted-foreground">{optVal}</span>
                              <span className="font-medium text-foreground">{count}×</span>
                            </div>
                          ))}
                        </div>
                      )
                      const unit = entry.mode === 'sum' && fieldDef?.unit ? ` ${fieldDef.unit}` : ''
                      const display = entry.mode === 'count' ? `${entry.value}×` : `${entry.value}${unit}`
                      return (
                        <div key={fieldKey} className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">{lbl}</span>
                          <span className="font-medium text-foreground">{display}</span>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      })()}

      {/* ── Monatszusammenfassung ───────────────────────────── */}
      {(() => {
        const diaeten = sumDiaeten(monthDailyRecords)
        const diaetenEntries = Object.entries(diaeten).filter(([, v]) =>
          v.mode === 'grouped' ? Object.keys(v.groups).length > 0 : v.value !== 0
        )
        const currentYearMonthVal = new Date(monthAnchor + 'T00:00:00').toISOString().slice(0, 7)
        const isCurrentMonth = currentYearMonthVal >= todayYearMonth
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-semibold text-primary">Monatszusammenfassung</h2>
            <div className="relative rounded-lg border border-border bg-background shadow-sm">
              <LoadingOverlay show={monthLoading} />
              <div className="flex items-center gap-2 border-b border-border px-4 py-3">
                <button type="button" onClick={navigatePrevMonth} className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
                  <span className="text-4xl leading-none">‹</span>
                </button>
                <div className="flex flex-1 items-center gap-2">
                  <h3 className="flex-1 text-center text-base font-semibold text-primary">{formatMonthHeader(monthAnchor)}</h3>
                  <input
                    type="month" value={currentYearMonthVal} max={todayYearMonth}
                    onChange={(e) => { if (e.target.value) setMonthAnchor(e.target.value + '-01') }}
                    className="rounded-md border border-input bg-background px-2 py-1 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    title="Zu einem bestimmten Monat springen"
                  />
                </div>
                <button type="button" onClick={navigateNextMonth} disabled={isCurrentMonth}
                  className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                  <span className="text-4xl leading-none">›</span>
                </button>
              </div>
              <div className="p-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Gesamtarbeitszeit</span>
                  <span className="font-medium text-foreground">{monthSummary != null ? formatHoursDecimal(monthSummary.totalMinutes) : '–'}</span>
                </div>
                {/* AP-19: Fehlzeiten je Typ */}
                {(() => {
                  const absenceSums = sumAbsencesByType(monthAbsences)
                  return absenceSums.length > 0 ? (
                    <div className="space-y-1 border-t border-border pt-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-primary pl-1 pb-1">Fehlzeiten</p>
                      {absenceSums.map((a) => (
                        <div key={a.name} className="flex items-center justify-between text-sm pl-1">
                          <span className="text-muted-foreground flex items-center gap-1.5">
                            <span className="inline-block h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: a.color ?? '#6b7280' }} />
                            {a.name}
                          </span>
                          <span className="font-medium text-foreground">{formatHoursDecimal(a.minutes)}</span>
                        </div>
                      ))}
                    </div>
                  ) : null
                })()}
                {diaetenEntries.length > 0 && (
                  <div className="space-y-1 border-t border-border pt-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-primary pl-1 pb-1">Diäten</p>
                    {diaetenEntries.map(([fieldKey, entry]) => {
                      const fieldDef = dailyRecordSchema.find((f) => f.name === fieldKey)
                      const lbl = fieldDef?.label ?? fieldKey
                      if (entry.mode === 'grouped') return (
                        <div key={fieldKey} className="space-y-0.5">
                          <span className="text-sm text-muted-foreground">{lbl}</span>
                          {Object.entries(entry.groups).map(([optVal, count]) => (
                            <div key={optVal} className="flex items-center justify-between text-sm pl-3">
                              <span className="text-muted-foreground">{optVal}</span>
                              <span className="font-medium text-foreground">{count}×</span>
                            </div>
                          ))}
                        </div>
                      )
                      const unit = entry.mode === 'sum' && fieldDef?.unit ? ` ${fieldDef.unit}` : ''
                      const display = entry.mode === 'count' ? `${entry.value}×` : `${entry.value}${unit}`
                      return (
                        <div key={fieldKey} className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">{lbl}</span>
                          <span className="font-medium text-foreground">{display}</span>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      })()}

      {/* ── Jahreszusammenfassung ───────────────────────────── */}
      {(() => {
        const diaeten = sumDiaeten(yearDailyRecords)
        const diaetenEntries = Object.entries(diaeten).filter(([, v]) =>
          v.mode === 'grouped' ? Object.keys(v.groups).length > 0 : v.value !== 0
        )
        const yearVal = new Date(yearAnchor + 'T00:00:00').getFullYear()
        const isCurrentYear = yearVal >= todayYear
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-semibold text-primary">Jahreszusammenfassung</h2>
            <div className="relative rounded-lg border border-border bg-background shadow-sm">
              <LoadingOverlay show={yearLoading} />
              <div className="flex items-center gap-2 border-b border-border px-4 py-3">
                <button type="button" onClick={navigatePrevYear} className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
                  <span className="text-4xl leading-none">‹</span>
                </button>
                <div className="flex flex-1 items-center gap-2">
                  <h3 className="flex-1 text-center text-base font-semibold text-primary">{formatYearHeader(yearAnchor)}</h3>
                  <input
                    type="number" value={yearVal} min={2000} max={todayYear}
                    onChange={(e) => { const y = parseInt(e.target.value); if (y >= 2000 && y <= todayYear) setYearAnchor(`${y}-01-01`) }}
                    className="w-20 rounded-md border border-input bg-background px-2 py-1 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    title="Zu einem bestimmten Jahr springen"
                  />
                </div>
                <button type="button" onClick={navigateNextYear} disabled={isCurrentYear}
                  className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                  <span className="text-4xl leading-none">›</span>
                </button>
              </div>
              <div className="p-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Gesamtarbeitszeit</span>
                  <span className="font-medium text-foreground">{yearSummary != null ? formatHoursDecimal(yearSummary.totalMinutes) : '–'}</span>
                </div>
                {/* AP-19: Fehlzeiten je Typ */}
                {(() => {
                  const absenceSums = sumAbsencesByType(yearAbsences)
                  return absenceSums.length > 0 ? (
                    <div className="space-y-1 border-t border-border pt-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-primary pl-1 pb-1">Fehlzeiten</p>
                      {absenceSums.map((a) => (
                        <div key={a.name} className="flex items-center justify-between text-sm pl-1">
                          <span className="text-muted-foreground flex items-center gap-1.5">
                            <span className="inline-block h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: a.color ?? '#6b7280' }} />
                            {a.name}
                          </span>
                          <span className="font-medium text-foreground">{formatHoursDecimal(a.minutes)}</span>
                        </div>
                      ))}
                    </div>
                  ) : null
                })()}
                {diaetenEntries.length > 0 && (
                  <div className="space-y-1 border-t border-border pt-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-primary pl-1 pb-1">Diäten</p>
                    {diaetenEntries.map(([fieldKey, entry]) => {
                      const fieldDef = dailyRecordSchema.find((f) => f.name === fieldKey)
                      const lbl = fieldDef?.label ?? fieldKey
                      if (entry.mode === 'grouped') return (
                        <div key={fieldKey} className="space-y-0.5">
                          <span className="text-sm text-muted-foreground">{lbl}</span>
                          {Object.entries(entry.groups).map(([optVal, count]) => (
                            <div key={optVal} className="flex items-center justify-between text-sm pl-3">
                              <span className="text-muted-foreground">{optVal}</span>
                              <span className="font-medium text-foreground">{count}×</span>
                            </div>
                          ))}
                        </div>
                      )
                      const unit = entry.mode === 'sum' && fieldDef?.unit ? ` ${fieldDef.unit}` : ''
                      const display = entry.mode === 'count' ? `${entry.value}×` : `${entry.value}${unit}`
                      return (
                        <div key={fieldKey} className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">{lbl}</span>
                          <span className="font-medium text-foreground">{display}</span>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      })()}

    </div>
    </>
  )
}
